<template>
  <section class="card">
    <div class="page-head">
      <button
        type="button"
        class="back-btn"
        title="返回活动列表"
        @click="goBack"
        @mouseenter="backBtnHover = true"
        @mouseleave="backBtnHover = false"
      >
        <img
          class="back-btn-icon"
          :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
          alt="返回"
        >
      </button>
      <h2>活动报名名单</h2>
    </div>
    <p class="sub-line">{{ pageTitle }}</p>

    <p class="tip">
      主表按<strong>参会人一人一行</strong>展示；<strong>身份</strong>根据该行填写的单位名称是否已在协会正式会员库匹配（入会=会员，否则=非会员）。
      <strong>报名者信息</strong>为提交该条报名表的小程序账号。<strong>活动状态</strong>、<strong>订单状态</strong>与缴费凭证均按<strong>每位参会人独立</strong>；已用手机号注册会员的，在其本人「我的订单」上传凭证。代报未注册者由报名账号或会务线下处理。
      操作列可直接审核报名信息、确认缴费等，无需再打开「查看报名信息」弹窗。
      订单状态为<strong>待支付</strong>时，标签下会提示线上/线下缴费方式；<strong>审核缴费信息中</strong>且会员已线上支付时，标签下提示待会务确认，操作列仍可<strong>帮他上传缴费凭证</strong>。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div>
    <div class="toolbar-row">
      <button type="button" class="btn toolbar-btn toolbar-btn--export-checkin" :disabled="!activityId || exportingCheckin" @click="exportCheckedInRoster">
        {{ exportingCheckin ? '导出中…' : '导出已签到名单 CSV' }}
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--export-roster" :disabled="!activityId || exportingRoster" @click="openRosterExportDialog">
        {{ exportingRoster ? '导出中…' : '自定义导出 Excel' }}
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--download-template" :disabled="!activityId || importingSignup" @click="downloadSignupTemplate">
        下载导入格式
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--import-roster" :disabled="!activityId || importingSignup" @click="pickSignupImport">
        {{ importingSignup ? '导入中…' : '导入报名名单' }}
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--proxy-signup" :disabled="!activityId" @click="openProxySignupDlg">
        会务代报
      </button>
      <input ref="signupImportInput" type="file" accept=".xlsx,.xls,.csv" class="hidden-file" @change="onSignupImportFile" />
      <button type="button" class="btn toolbar-btn toolbar-btn--seating" :disabled="!activityId" @click="goSeating">
        排座
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--lottery" :disabled="!activityId" @click="goLottery">
        现场抽奖
      </button>
      <button type="button" class="btn toolbar-btn toolbar-btn--satisfaction" :disabled="!activityId" @click="goSatisfactionResponses">
        问卷答卷
      </button>
    </div>

    <div class="checkin-mode-banner">
      <strong>签到说明</strong>
      <p>
        参会人可扫活动编辑页下载的<strong>签到处场所码</strong>自助签到；会务也可在下方粘贴/扫描会员<strong>电子凭证</strong>代签。
      </p>
    </div>

    <div class="staff">
      <label class="staff-label">粘贴核验串代签到（会员电子凭证页复制）</label>
      <textarea v-model.trim="staffPaste" class="staff-ta" rows="2" placeholder="HY_ACT|活动编号|会员编号|…" />
      <p class="staff-hint">
        在<strong>电脑浏览器</strong>打开本后台时，可用下方摄像头扫会员小程序「电子凭证」上的二维码填入核验串；用手机打开后台时也可手输核验串。
      </p>
      <div class="staff-row">
        <button type="button" class="btn btn-edit" @click="toggleStaffQrScan">
          {{ staffQrActive ? '关闭摄像头' : '摄像头扫码填入' }}
        </button>
        <span v-if="staffQrErr" class="staff-err">{{ staffQrErr }}</span>
      </div>
      <div v-if="staffQrActive" id="staff-qr-reader" class="staff-qr-reader" />
      <div class="staff-row">
        <button type="button" class="btn btn-edit" :disabled="!activityId || staffChecking" @click="doStaffCheckin">
          {{ staffChecking ? '提交中…' : '确认代签到' }}
        </button>
        <span v-if="staffCheckinMsg" class="staff-msg">{{ staffCheckinMsg }}</span>
      </div>
    </div>

    <div class="filters filters-panel roster-filters">
      <div class="filter-row">
        <div class="flt">
          <FancySelect
            v-model="filterSourceLabel"
            class="flt-select"
            :options="sourceLabelFilterOptions"
            placeholder="身份"
          />
        </div>
        <div class="flt">
          <input
            v-model.trim="regFilterAttendeeKeyword"
            class="flt-input flt-input--attendee"
            placeholder="姓名 / 手机 / 单位 / 职务"
            aria-label="姓名、手机、单位、职务"
          >
        </div>
        <div class="flt">
          <FancySelect
            v-model="filterActivityStatus"
            class="flt-select"
            :options="activityStatusFilterOptions"
            placeholder="活动状态"
          />
        </div>
        <div class="flt">
          <FancySelect
            v-model="filterOrderStatusText"
            class="flt-select"
            :options="orderStatusFilterOptions"
            placeholder="订单状态"
          />
        </div>
        <div class="flt">
          <input
            v-model.trim="regFilterSubmitter"
            class="flt-input flt-input--wide"
            placeholder="报名者信息"
            aria-label="报名者信息"
          >
        </div>
        <div class="flt">
          <FancySelect
            v-model="filterMemberTagId"
            class="flt-select"
            :options="memberTagFilterOptions"
            placeholder="标签"
          />
        </div>
      </div>
      <div class="filter-row filter-row--secondary">
        <div class="flt flt--range-inline">
          <span class="flt-range-label">报名时间</span>
          <span class="flt-range">
            <input v-model="filterSubmittedFrom" type="date" class="flt-input flt-input--date" aria-label="报名开始日期">
            <span class="flt-sep">至</span>
            <input v-model="filterSubmittedTo" type="date" class="flt-input flt-input--date" aria-label="报名结束日期">
          </span>
        </div>
        <div class="flt flt--range-inline">
          <span class="flt-range-label">取消报名时间</span>
          <span class="flt-range">
            <input v-model="filterCancelledFrom" type="date" class="flt-input flt-input--date" aria-label="取消开始日期">
            <span class="flt-sep">至</span>
            <input v-model="filterCancelledTo" type="date" class="flt-input flt-input--date" aria-label="取消结束日期">
          </span>
        </div>
        <div class="filter-actions">
          <button type="button" class="btn btn-primary filter-action-btn" :disabled="!activityId" @click="onSearch">查询</button>
          <button type="button" class="btn btn-cancel filter-action-btn" @click="onResetFilters">重置</button>
        </div>
      </div>
    </div>

    <div v-if="rosterSelectMode" class="roster-batch-bar">
      <span class="roster-batch-tip">已选 {{ selectedRosterMemberIds.length }} 人（仅含可打标签的会员账号）</span>
      <button type="button" class="btn btn-edit btn-sm" @click="toggleRosterSelectAll">
        {{ rosterAllSelectableChecked ? '取消全选' : '全选可打标签' }}
      </button>
      <button type="button" class="btn btn-primary btn-sm" :disabled="!selectedRosterMemberIds.length" @click="openRosterTagDialog()">
        批量打标签
      </button>
      <button type="button" class="btn btn-cancel btn-sm" @click="exitRosterSelectMode">完成</button>
    </div>
    <div v-else class="roster-batch-bar roster-batch-bar--idle">
      <button type="button" class="btn btn-edit btn-sm" @click="enterRosterSelectMode">批量操作</button>
    </div>

    <div class="table-scroll">
      <table class="roster-table">
        <thead>
          <tr>
            <th v-if="rosterSelectMode" class="col-check">
              <input type="checkbox" :checked="rosterAllSelectableChecked" @change="toggleRosterSelectAll" />
            </th>
            <th class="col-seq">序号</th>
            <th class="col-status col-center">身份</th>
            <th class="col-name col-center">姓名</th>
            <th class="col-phone col-center">手机号</th>
            <th class="col-company col-center">单位名称</th>
            <th class="col-duty col-center">单位职务</th>
            <th class="col-status col-center">活动状态</th>
            <th class="col-status col-center">订单状态</th>
            <th class="col-datetime col-center">报名时间</th>
            <th class="col-datetime col-center">取消报名时间</th>
            <th class="col-submitter col-center">报名者信息</th>
            <th class="col-tags col-center">标签</th>
            <th class="col-sticky-right col-ops">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in pagedAttendeeRows"
            :id="attendeeRowDomId(row)"
            :key="row.rowKey || `ar-${idx}`"
            :class="{ 'roster-row--focus': isFocusedAttendeeRow(row) }"
          >
            <td v-if="rosterSelectMode" class="col-check">
              <input
                type="checkbox"
                :disabled="!rosterRowMemberId(row)"
                :checked="isRosterRowSelected(row)"
                @change="toggleRosterRowSelected(row)"
              />
            </td>
            <td class="col-seq">{{ rosterRowOffset + idx + 1 }}</td>
            <td class="col-status col-center">
              <span class="status-pill" :class="identityStatusClass(row.identityLabel)">{{ row.identityLabel || '—' }}</span>
            </td>
            <td class="col-name col-center">
              <div>{{ row.attendeeName || '—' }}</div>
              <div v-if="attendeeSignupChannelLabel(row)" class="channel-tag">{{ attendeeSignupChannelLabel(row) }}</div>
              <div v-if="row.partitionNote" class="sub">{{ row.partitionNote }}</div>
            </td>
            <td class="col-phone col-center">{{ row.attendeePhone || '—' }}</td>
            <td class="col-company col-center">{{ row.attendeeCompany || '—' }}</td>
            <td class="col-duty col-center">{{ row.attendeeDuty || '—' }}</td>
            <td class="col-status col-center">
              <span class="status-pill" :class="activityStatusClass(row.activityStatusLabel)">{{ row.activityStatusLabel || '—' }}</span>
              <div v-if="attendeeRowCheckinAtDisplay(row)" class="activity-checkin-at">
                {{ attendeeRowCheckinAtDisplay(row) }}
              </div>
            </td>
            <td class="col-status col-center">
              <span class="status-pill" :class="orderStatusClass(row.orderStatusText)">{{ row.orderStatusText || '—' }}</span>
              <div v-if="orderStatusSubline(row)" class="order-cancel-at">
                {{ orderStatusSubline(row) }}
              </div>
            </td>
            <td class="col-datetime col-center">{{ rosterDateTimeDisplay(row.submittedAt) }}</td>
            <td class="col-datetime col-center">{{ attendeeSignupCancelledAtDisplay(row) }}</td>
            <td class="col-submitter col-center">{{ row.submitterInfo || '—' }}</td>
            <td class="col-tags col-center">
              <span v-for="name in rosterRowTagNames(row)" :key="name" class="member-tag-pill">{{ name }}</span>
              <span v-if="!rosterRowTagNames(row).length" class="muted">—</span>
            </td>
            <td class="col-sticky-right col-ops">
              <div class="action-stack">
                <button
                  v-if="attendeeRowSignupSubmissionId(row)"
                  type="button"
                  class="btn btn-act btn-act-edit"
                  :disabled="!canAct"
                  @click="goEditSignupPayload(row)"
                >
                  编辑
                </button>
                <button
                  v-if="rosterRowMemberId(row)"
                  type="button"
                  class="btn btn-act btn-act-tag"
                  @click="openRosterTagDialog(row)"
                >
                  打标签
                </button>
                <button
                  type="button"
                  class="btn btn-act btn-act-audit-signup"
                  @click="openSignupSubmissionAuditFromAttendee(row)"
                >
                  审核报名信息
                </button>
                <button
                  v-if="attendeeRowShowCancelReasonBtn(row)"
                  type="button"
                  class="btn btn-act"
                  :class="rosterCancelBtnClass(row)"
                  @click="openSignupCancelReasonDlg(row)"
                >
                  {{ attendeeRowCancelActionLabel(row) }}
                </button>
                <button
                  v-if="attendeeRowShowAdminUploadRefundVoucher(row)"
                  type="button"
                  class="btn btn-act btn-act-refund-upload"
                  :disabled="!canAct"
                  @click="openAdminUploadRefundVoucher(row)"
                >
                  上传退费凭证
                </button>
                <button
                  v-else-if="attendeeRowShowMarkRefundedBtn(row)"
                  type="button"
                  class="btn btn-act btn-act-refund-done"
                  :disabled="!canAct"
                  @click="submitMarkActivityFeeRefundedForAttendee(row)"
                >
                  确认已退回
                </button>
                <button
                  v-if="attendeeRowShowConfirmPay(row)"
                  type="button"
                  class="btn btn-act btn-act-confirm-pay"
                  :disabled="!canAct"
                  @click="onAttendeeRowConfirmPay(row)"
                >
                  确认缴费
                </button>
                <button
                  v-else-if="attendeeRowShowAdminUploadVoucher(row)"
                  type="button"
                  class="btn btn-act btn-act-upload-voucher"
                  @click="onAdminUploadVoucherClick(row)"
                >
                  {{ adminUploadVoucherBtnLabel(row) }}
                </button>
                <button
                  v-else-if="attendeeRowShowVoucherView(row)"
                  type="button"
                  class="btn btn-act btn-act-view-voucher"
                  @click="openVoucherAudit(regCtx(row), row)"
                >
                  查看凭证
                </button>
                <button
                  v-if="attendeeRowShowCheckinSignatureAction(row)"
                  type="button"
                  class="btn btn-act"
                  :class="rosterCheckinSigBtnClass(row)"
                  @click="openCheckinSignatureDlg(row)"
                >
                  {{ attendeeRowCheckinSignatureUrl(row) ? '电子签名' : '补传签名' }}
                </button>
                <button
                  v-if="activityEnableBadgePrint"
                  type="button"
                  class="btn btn-act btn-act-badge"
                  @click="openAttendeeNameBadge(row)"
                >
                  胸卡
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="!pagedAttendeeRows.length" class="muted empty-hint">无匹配记录。</p>
    <ListPager
      v-else
      v-model:page="rosterPage"
      v-model:page-size="rosterPageSize"
      :total="rosterFilteredTotal"
    />
    </div>

    <div v-if="showVoucherAudit" class="mask" @click.self="closeVoucherAudit">
      <div class="dialog dialog-voucher">
        <div class="dialog-head">
          <h3>{{ voucherAuditTitle }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeVoucherAudit">×</button>
        </div>
        <p class="dlg-meta">{{ voucherAuditMeta }}</p>
        <div class="voucher-status-row">
          <span class="status-pill" :class="activityStatusClass(voucherAuditActivityStatus)">活动状态：{{ voucherAuditActivityStatus }}</span>
          <span class="status-pill" :class="orderStatusClass(voucherAuditOrderStatusZh)">订单状态：{{ voucherAuditOrderStatusZh }}</span>
        </div>
        <template v-if="voucherAuditMode === 'view'">
          <div v-for="sec in voucherAuditViewSections" :key="sec.key" class="voucher-view-block">
            <h4 class="voucher-view-title">{{ sec.title }}</h4>
            <div class="voucher-img-wrap">
              <img v-if="sec.url" class="voucher-img" :src="sec.url" :alt="sec.title">
              <p v-else class="muted">{{ sec.empty }}</p>
            </div>
          </div>
        </template>
        <div v-else class="voucher-img-wrap">
          <img v-if="voucherAuditImg" class="voucher-img" :src="voucherAuditImg" alt="凭证">
          <p v-else-if="voucherAuditMode === 'admin-upload'" class="muted">请选择线下缴费截图后，再点击「确认缴费通过」。</p>
          <p v-else-if="voucherAuditMode === 'admin-refund'" class="muted">请选择线下退费截图后，再点击「确认退回」。</p>
          <p v-else class="muted">暂无凭证截图（若需仍可驳回并让会员重新上传）。</p>
        </div>
        <p v-if="voucherAuditDialogMsg" class="error voucher-dialog-error">{{ voucherAuditDialogMsg }}</p>
        <div v-if="voucherAuditMode === 'admin-upload'" class="dialog-actions dialog-actions--upload">
          <input
            ref="adminVoucherFileInput"
            type="file"
            accept="image/*,application/pdf,.doc,.docx"
            class="hidden-file"
            @change="onAdminVoucherFilePicked"
          />
          <button
            type="button"
            class="btn btn-edit"
            :disabled="!canAct || voucherApproveBusy"
            @click="pickAdminVoucherFile"
          >
            选择凭证图片
          </button>
            <button
              type="button"
            class="btn btn-primary"
            :disabled="!canAct || voucherApproveBusy || !adminUploadVoucherUrl"
            @click="confirmAdminUploadAndApprove"
          >
            {{ voucherApproveBusy ? '提交中…' : '确认缴费通过' }}
            </button>
          </div>
        <div v-else-if="voucherAuditMode === 'admin-refund'" class="dialog-actions dialog-actions--upload">
          <input
            ref="adminRefundVoucherFileInput"
            type="file"
            accept="image/*,application/pdf,.doc,.docx"
            class="hidden-file"
            @change="onAdminVoucherFilePicked"
          />
          <button
            type="button"
            class="btn btn-edit"
            :disabled="!canAct || voucherApproveBusy"
            @click="pickAdminRefundVoucherFile"
          >
            选择凭证图片
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canAct || voucherApproveBusy"
            @click="confirmAdminRefund"
          >
            {{ voucherApproveBusy ? '提交中…' : '确认退回' }}
          </button>
        </div>
        <div v-else-if="voucherAuditCanReview" class="dialog-actions">
          <button type="button" class="btn btn-primary" :disabled="!canAct || voucherApproveBusy" @click="fromVoucherApprove">
            {{ voucherApproveBusy ? '提交中…' : '缴费通过' }}
          </button>
          <button type="button" class="btn btn-delete" :disabled="!canAct" @click="fromVoucherReject">
            缴费不通过
          </button>
          </div>
          </div>
        </div>

    <div v-if="signupCancelReasonDlg.visible" class="mask" @click.self="closeSignupCancelReasonDlg">
      <div class="dialog dlg-cancel-reason" @click.stop>
        <div class="dialog-head">
          <h3>{{ signupCancelReasonDlg.pending ? '审核取消报名' : (signupCancelReasonDlg.rejectNote ? '取消报名驳回记录' : '取消报名记录') }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeSignupCancelReasonDlg">×</button>
        </div>
        <p v-if="signupCancelReasonDlg.meta" class="dlg-meta">{{ signupCancelReasonDlg.meta }}</p>
        <div v-if="signupCancelReasonDlg.attendeeLine" class="dlg-audit-summary">
          <div><strong>本行参会人</strong> {{ signupCancelReasonDlg.attendeeLine }}</div>
        </div>
        <p v-if="signupCancelReasonDlg.mismatchWarn" class="dlg-meta dlg-meta--warn">
          {{ signupCancelReasonDlg.mismatchWarn }}
        </p>
        <div v-if="signupCancelReasonDlg.pending" class="dlg-audit-summary">
          <div><strong>申请时间</strong> {{ signupCancelReasonDlg.requestAt || '—' }}</div>
          <div><strong>申请人</strong> {{ signupCancelReasonDlg.requestBy || '—' }}</div>
          <div v-if="signupCancelReasonDlg.requestOperatorMobile">
            <strong>申请人手机号</strong> {{ signupCancelReasonDlg.requestOperatorMobile }}
          </div>
        </div>
        <template v-else-if="signupCancelReasonDlg.rejected">
          <p v-if="signupCancelReasonDlg.requestAt" class="dlg-meta">
            申请取消时间：{{ signupCancelReasonDlg.requestAt }}
          </p>
          <p v-if="signupCancelReasonDlg.rejectAt" class="dlg-meta">驳回时间：{{ signupCancelReasonDlg.rejectAt }}</p>
        </template>
        <template v-else>
          <p v-if="signupCancelReasonDlg.cancelledAt" class="dlg-meta">
            取消报名时间：{{ signupCancelReasonDlg.cancelledAt }}
          </p>
          <p v-if="signupCancelReasonDlg.cancelledBy" class="dlg-meta">取消人：{{ signupCancelReasonDlg.cancelledBy }}</p>
        </template>
        <h4 class="dlg-audit-subtitle">取消原因</h4>
        <p class="dlg-cancel-reason-body">{{ signupCancelReasonDlg.cancelReason || '（未填写原因）' }}</p>
        <template v-if="signupCancelReasonDlg.rejectNote && !signupCancelReasonDlg.pending">
          <h4 class="dlg-audit-subtitle">驳回原因</h4>
          <p class="dlg-cancel-reason-body dlg-cancel-reason-body--reject">{{ signupCancelReasonDlg.rejectNote }}</p>
        </template>
        <label v-if="signupCancelReasonDlg.pending" class="field dlg-audit-field">
          <span>驳回原因（必填，会员端可见）</span>
          <textarea v-model="signupCancelReasonDlg.rejectNote" placeholder="请填写驳回原因" rows="2" />
        </label>
        <div v-if="signupCancelReasonDlg.pending" class="dialog-actions dlg-audit-actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canAct || signupCancelReasonDlg.busy"
            @click="submitApproveSignupCancelRequest"
          >
            {{ signupCancelReasonDlg.busy ? '处理中…' : '通过' }}
          </button>
          <button
            type="button"
            class="btn btn-delete"
            :disabled="!canAct || signupCancelReasonDlg.busy"
            @click="submitRejectSignupCancelRequest"
          >
            {{ signupCancelReasonDlg.busy ? '提交中…' : '确认驳回' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="signupSubmissionAuditVisible"
      class="mask mask--submission-audit"
      @click.self="closeSignupSubmissionAudit"
    >
      <div class="dialog dialog-signup-preview dlg-submission-audit">
        <div class="dialog-head">
          <h3 class="dialog-title">审核报名信息</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeSignupSubmissionAudit">×</button>
        </div>
        <template v-if="submissionAuditTableRow">
          <p class="dlg-meta">{{ submissionAuditMeta }}</p>
          <div class="dlg-audit-summary">
            <div><strong>姓名</strong> {{ submissionAuditTableRow.name }}</div>
            <div><strong>手机</strong> {{ submissionAuditTableRow.phone }}</div>
            <div><strong>单位</strong> {{ submissionAuditTableRow.company }}</div>
            <div><strong>职务</strong> {{ submissionAuditTableRow.duty }}</div>
        </div>
          <h4 class="dlg-audit-subtitle">活动信息收集</h4>
          <div v-if="submissionAuditCollectFields.length" class="dlg-collect-list">
            <div v-for="(cf, ci) in submissionAuditCollectFields" :key="'cf-' + ci" class="dlg-collect-row">
              <span class="dlg-collect-l">{{ cf.label }}</span>
              <span class="dlg-collect-v">{{ cf.value }}</span>
      </div>
    </div>
          <p v-else class="muted">暂无额外收集项（或已全部体现在摘要列）。</p>
          <label v-if="submissionAuditShowRejectNote" class="field dlg-audit-field">
            <span>驳回原因（可选，会员端可见）</span>
            <textarea v-model="submissionAuditRejectNote" placeholder="可空" rows="2" />
          </label>
          <div v-if="submissionAuditShowRejectReasonView" class="dlg-reject-reason-view">
            <h4 class="dlg-audit-subtitle">驳回原因</h4>
            <p v-if="submissionAuditRejectReviewedAt" class="dlg-meta">
              驳回时间：{{ submissionAuditRejectReviewedAt }}
            </p>
            <p class="dlg-cancel-reason-body">{{ submissionAuditRejectReasonText }}</p>
        </div>
          <div class="dialog-actions dlg-audit-actions">
                  <button
              v-if="submissionAuditShowRejectNote || submissionAuditShowRejectReasonView"
                    type="button"
              class="btn btn-edit"
              @click="closeSubmissionAuditRejectPanels"
            >
              返回
            </button>
                  <button
              v-if="submissionAuditCanViewRejectReason && !submissionAuditShowRejectNote && !submissionAuditShowRejectReasonView"
                    type="button"
                    class="btn btn-edit"
                    @click="submissionAuditShowRejectReasonView = true"
            >
              查看驳回原因
            </button>
                  <button
              v-if="submissionAuditCanRejectSignup && !submissionAuditShowRejectNote && !submissionAuditShowRejectReasonView"
                    type="button"
                    class="btn btn-delete"
                    :disabled="!canAct"
              @click="submissionAuditShowRejectNote = true"
            >
              驳回报名
            </button>
            <button
              v-if="submissionAuditShowRejectNote"
              type="button"
              class="btn btn-delete"
              :disabled="!canAct || submissionAuditRejectBusy"
              @click="submitSubmissionAuditReject"
            >
              {{ submissionAuditRejectBusy ? '提交中…' : '确认驳回报名' }}
            </button>
            <button
              v-if="submissionAuditCanApprove && !submissionAuditShowRejectNote && !submissionAuditShowRejectReasonView"
              type="button"
              class="btn btn-primary"
              :disabled="!canAct || signupPreviewSubmissionBusy === submissionAuditTableRow.partitionSubmissionId"
              @click="submitSubmissionAuditApprove"
            >
              {{
                signupPreviewSubmissionBusy === submissionAuditTableRow.partitionSubmissionId
                  ? '提交中…'
                  : '报名信息通过'
              }}
            </button>
        </div>
        </template>
      </div>
    </div>

    <ActivitySignupEditDialog
      :visible="signupEditDlg.visible"
      :activity-id="activityId"
      :submission-id="signupEditDlg.submissionId"
      :initial-slot-index="signupEditDlg.initialSlotIndex"
      :can-act="canAct"
      @close="closeSignupEditDlg"
      @saved="onSignupEditSaved"
    />

    <ActivityProxySignupDialog
      :visible="proxySignupDlg.visible"
      :activity-id="activityId"
      @close="closeProxySignupDlg"
      @saved="onProxySignupSaved"
    />

    <div v-if="nameBadgeDlg.visible" class="mask" @click.self="closeNameBadgeDlg">
      <div class="dialog dialog--shell dialog-badge-preview" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">胸卡预览</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeNameBadgeDlg">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">{{ nameBadgeDlg.meta }}</p>
          <p class="badge-template-hint">
            正面字号、颜色与位置由<strong>胸卡模板</strong>决定，本弹窗仅预览。需要调整请
            <button type="button" class="btn-link" @click="goEditBadgeTemplate">打开模板管理编辑</button>
            （可拖动文字、改字号；保存后重新打开本预览）。
          </p>
          <div v-if="nameBadgeDlg.card && nameBadgeDlg.card.available === false && nameBadgeDlg.card.message" class="badge-preview-loading">
            {{ nameBadgeDlg.card.message }}
          </div>
          <div v-else id="admin-badge-print-area" class="badge-print-target">
            <ActivityBadgeCardPreview :card="nameBadgeDlg.card" :show-back="true" duplex-layout="stack" />
          </div>
          <p class="badge-print-note">
            推荐「下载 Word（2 页）」：正面一页、反面一页，Word 双面打印即可。名单打印含真实扫码链接。
          </p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-primary" @click="exportNameBadgeWord">下载 Word（2 页）</button>
          <button type="button" class="btn btn-edit" @click="printNameBadgeDlg">浏览器打印</button>
        </div>
      </div>
    </div>

    <div v-if="checkinSignatureDlg.visible" class="mask" @click.self="closeCheckinSignatureDlg">
      <div class="dialog dialog-voucher">
        <div class="dialog-head">
          <h3>电子签名</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeCheckinSignatureDlg">×</button>
        </div>
        <p class="dlg-meta">{{ checkinSignatureDlg.meta }}</p>
        <div class="voucher-img-wrap">
          <img
            v-if="checkinSignatureDlg.previewUrl"
            class="voucher-img"
            :src="checkinSignatureDlg.previewUrl"
            alt="电子签名"
          >
          <p v-else class="muted">暂无签名图片，可下方补传（按该行参会人槽位保存，不会影响其他人）。</p>
        </div>
        <p v-if="checkinSignatureDlg.err" class="error voucher-dialog-error">{{ checkinSignatureDlg.err }}</p>
        <div v-if="canAct" class="dialog-actions dialog-actions--upload">
          <input
            ref="adminCheckinSignatureFileInput"
            type="file"
            accept="image/*"
            class="hidden-file"
            @change="onAdminCheckinSignatureFilePicked"
          />
          <button
            type="button"
            class="btn btn-edit"
            :disabled="checkinSignatureDlg.busy"
            @click="pickAdminCheckinSignatureFile"
          >
            选择签名图片
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="checkinSignatureDlg.busy || !checkinSignatureDlg.uploadUrl"
            @click="saveAdminCheckinSignature"
          >
            {{ checkinSignatureDlg.busy ? '保存中…' : '保存到本行' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showReject" class="mask">
      <div class="dialog">
        <h3>缴费不通过</h3>
        <label class="field">
          <span>驳回原因（必填）</span>
          <textarea v-model="rejectNote" placeholder="会员端可见" />
        </label>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showReject = false">取消</button>
          <button type="button" class="btn btn-delete" @click="submitReject">确认驳回</button>
        </div>
      </div>
    </div>

    <div v-if="exportDlg.visible" class="mask" @click.self="closeRosterExportDialog">
      <div class="dialog dialog--shell dialog--lg" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">自定义导出 Excel</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeRosterExportDialog">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">将导出<strong>当前筛选结果</strong>（共 {{ filteredAttendeeRows.length }} 行），字段勾选会记住到本活动。</p>
          <div class="export-field-toolbar">
            <button type="button" class="btn btn-edit btn-sm" @click="selectAllExportFields">全选</button>
            <button type="button" class="btn btn-edit btn-sm" @click="clearExportFields">清空</button>
            <button type="button" class="btn btn-edit btn-sm" @click="resetExportFieldsDefault">恢复默认</button>
          </div>
          <div v-if="exportDlg.loading" class="muted">加载字段…</div>
          <div v-else class="export-field-groups">
            <div v-for="g in exportFieldGroups" :key="g.name" class="export-field-group">
              <div class="export-field-group-title">{{ g.name }}</div>
              <div class="tag-chip-grid">
                <label
                  v-for="f in g.items"
                  :key="f.key"
                  class="tag-chip"
                  :class="{ 'tag-chip--on': exportDlg.selectedKeys.includes(f.key) }"
                >
                  <input v-model="exportDlg.selectedKeys" type="checkbox" :value="f.key">
                  <span>{{ f.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="exportDlg.busy" @click="closeRosterExportDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="exportDlg.busy || !exportDlg.selectedKeys.length" @click="submitRosterExport">
            {{ exportDlg.busy ? '导出中…' : '下载 Excel' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="tagDlg.visible" class="mask" @click.self="closeRosterTagDialog">
      <div class="dialog dialog--shell" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ tagDlg.replaceExisting ? '打标签' : '批量打标签' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeRosterTagDialog">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">已选 {{ tagDlg.memberIds.length }} 个会员账号</p>
          <div class="tag-dialog-toolbar">
            <span class="tag-dialog-label">全部标签（可多选）</span>
            <button type="button" class="btn btn-primary btn-sm" :disabled="tagDlg.busy" @click="promptNewMemberTag">新增标签</button>
          </div>
          <div class="tag-chip-grid">
            <label v-for="tag in memberTags" :key="tag.id" class="tag-chip" :class="{ 'tag-chip--on': tagDlg.selectedTagIds.includes(tag.id) }">
              <input v-model="tagDlg.selectedTagIds" type="checkbox" :value="tag.id">
              <span>{{ tag.tagName }}</span>
            </label>
            <p v-if="!memberTags.length" class="muted">暂无标签，请先新增</p>
          </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="tagDlg.busy" @click="closeRosterTagDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="tagDlg.busy || !tagDlg.selectedTagIds.length" @click="submitRosterTagDialog">确定</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm, adminPrompt } from '@/utils/adminDialog'
import {
  BASE_URL,
  approveAdminActivityMemberSignup,
  approveAdminActivitySignupSubmission,
  assignAdminAttendeeCheckinSignature,
  markAdminActivityFeeOrderRefunded,
  approveAdminMemberOrderOfflinePay,
  submitAdminActivityFeeOfflineVoucher,
  uploadImage,
  getAdminCmsActivity,
  getAdminCmsActivityRegistrationAttendees,
  rejectAdminActivitySignupSubmission,
  approveAdminSignupCancelRequest,
  rejectAdminSignupCancelRequest,
  rejectAdminMemberOrderOfflinePay,
  downloadAdminActivityCheckedInRosterCsv,
  downloadAdminActivityRosterExportXlsx,
  downloadAdminActivitySignupImportTemplate,
  fetchAdminActivityRosterExportFields,
  importAdminActivitySignupRoster,
  assignAdminMemberTags,
  createAdminMemberTag,
  getAdminMemberTags,
  staffCheckinActivity,
  getAdminAttendeeNameBadge,
} from '@/api'
import { hasAnyPermission } from '@/utils/adminPermission'
import { formatDateTimeForDisplay } from '@/utils/formatDateTime'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import ActivitySignupEditDialog from '@/components/ActivitySignupEditDialog.vue'
import ActivityProxySignupDialog from '@/components/ActivityProxySignupDialog.vue'
import ActivityBadgeCardPreview from '@/components/ActivityBadgeCardPreview.vue'
import { exportBadgeWord, printBadgeArea } from '@/utils/badgePrint.js'
import {
  groupRosterExportFields,
  persistRosterExportFieldKeys,
  resolveRosterExportSelectedKeys,
  buildRosterExportFieldOptions,
} from '@/utils/activityRosterExportFields.js'
import { totalPagesFor } from '@/utils/listPager'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

/** 活动状态（会务审核 + 缴费进度 + 签到，与订单状态分列） */
const ADMIN_ACTIVITY_STATUS_LABELS = [
  '已取消',
  '该报名被驳回',
  '审核报名信息中',
  '审核取消报名中',
  '待支付',
  '审核缴费信息中',
  '该缴费被驳回',
  '待签到',
  '已签到',
]

/**
 * 订单状态（活动费订单；报名未通过审核时为横杠）
 * - 该缴费被驳回：已传凭证，审核缴费不通过（可再付）
 * - 已取消：支付前关单（待付/审凭证中取消）
 * - 待退回/已退回：已支付后取消报名
 */
const ADMIN_ORDER_STATUS_LABELS = [
  '—',
  '待支付',
  '审核缴费信息中',
  '该缴费被驳回',
  '已支付',
  '已取消',
  '待退回',
  '已退回',
]

/** 状态标签：每个文案独立配色（相同文案跨列同色）。 */
const STATUS_PILL_CLASS_BY_LABEL = {
  '—': 'status-pill--dash',
  会员: 'status-pill--id-member',
  非会员: 'status-pill--id-guest',
  已取消: 'status-pill--cancelled',
  该报名被驳回: 'status-pill--signup-rejected',
  审核报名信息中: 'status-pill--signup-review',
  审核取消报名中: 'status-pill--cancel-review',
  待支付: 'status-pill--pending-pay',
  审核缴费信息中: 'status-pill--fee-review',
  该缴费被驳回: 'status-pill--fee-rejected',
  待签到: 'status-pill--await-checkin',
  已签到: 'status-pill--checked-in',
  已支付: 'status-pill--order-paid',
  待退回: 'status-pill--refund-pending',
  已退回: 'status-pill--refunded',
}

export default {
  name: 'ActivityRegistrationsManageView',
  components: { FancySelect, ListPager, ActivitySignupEditDialog, ActivityProxySignupDialog, ActivityBadgeCardPreview },
  data() {
    return {
      activityId: null,
      activityEnableBadgePrint: false,
      activityBadgeTemplateId: null,
      activityDetail: null,
      nameBadgeDlg: { visible: false, card: null, meta: '' },
      signupEditDlg: {
        visible: false,
        submissionId: 0,
        initialSlotIndex: 0,
      },
      proxySignupDlg: { visible: false },
      activityTitle: '',
      activityCheckinMode: 'STAFF_SCAN_ATTENDEE',
      activitySignupFeeCent: 0,
      attendeeRows: [],
      rosterPage: 1,
      rosterPageSize: 20,
      regFilterAttendeeKeyword: '',
      regFilterSubmitter: '',
      filterSourceLabel: '',
      filterActivityStatus: '',
      filterOrderStatusText: '',
      filterMemberTagId: '',
      filterSubmittedFrom: '',
      filterSubmittedTo: '',
      filterCancelledFrom: '',
      filterCancelledTo: '',
      voucherAuditAttendeeSnapshot: null,
      staffPaste: '',
      staffCheckinMsg: '',
      staffChecking: false,
      staffQrActive: false,
      staffQrErr: '',
      exportingCheckin: false,
      exportingRoster: false,
      importingSignup: false,
      rosterSelectMode: false,
      selectedRosterRowKeys: [],
      exportFieldOptions: [],
      exportDlg: {
        visible: false,
        loading: false,
        busy: false,
        selectedKeys: [],
      },
      memberTags: [],
      tagDlg: {
        visible: false,
        memberIds: [],
        selectedTagIds: [],
        replaceExisting: false,
        busy: false,
      },
      errorMsg: '',
      okMsg: '',
      canAct: false,
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
      signupPreviewSections: [],
      /** 当前操作关联的报名账号上下文（regContext） */
      signupPreviewRegRow: null,
      showReject: false,
      rejectOrderId: null,
      rejectNote: '',
      showVoucherAudit: false,
      voucherAuditMode: 'view',
      voucherAuditRow: null,
      voucherApproveBusy: false,
      adminUploadVoucherUrl: '',
      voucherAuditDialogMsg: '',
      signupSubmissionAuditVisible: false,
      submissionAuditTableRow: null,
      submissionAuditRejectNote: '',
      submissionAuditRejectBusy: false,
      submissionAuditShowRejectReasonView: false,
      submissionAuditShowRejectNote: false,
      /** 报名弹窗内「报名信息通过」防重复点击（值为 submissionId） */
      signupPreviewSubmissionBusy: '',
      signupCancelReasonDlg: {
        visible: false,
        pending: false,
        rejected: false,
        busy: false,
        showRejectInput: false,
        rejectNote: '',
        submissionId: 0,
        slotIndex: 0,
        meta: '',
        cancelReason: '',
        requestBy: '',
        requestAt: '',
        rejectAt: '',
        cancelledAt: '',
        cancelledBy: '',
        attendeeLine: '',
        requestOperatorMobile: '',
        mismatchWarn: '',
      },
      checkinSignatureDlg: {
        visible: false,
        previewUrl: '',
        uploadUrl: '',
        meta: '',
        err: '',
        busy: false,
        row: null,
      },
      focusRowKey: '',
      rosterPollTimer: null,
    }
  },
  computed: {
    sourceLabelFilterOptions() {
      return [
        { value: '', label: '全部身份' },
        { value: '会员', label: '会员' },
        { value: '非会员', label: '非会员' },
      ]
    },
    activityStatusFilterOptions() {
      return [
        { value: '', label: '全部活动状态' },
        ...ADMIN_ACTIVITY_STATUS_LABELS.map((v) => ({ value: v, label: v })),
      ]
    },
    orderStatusFilterOptions() {
      return [
        { value: '', label: '全部订单状态' },
        ...ADMIN_ORDER_STATUS_LABELS.map((v) => ({ value: v, label: v })),
      ]
    },
    memberTagFilterOptions() {
      const opts = [{ value: '', label: '全部标签' }]
      for (const tag of this.memberTags || []) {
        opts.push({ value: String(tag.id), label: tag.tagName })
      }
      return opts
    },
    pageTitle() {
      const t = String(this.activityTitle || '').trim()
      return t || (this.activityId ? `活动 ${this.activityId}` : '')
    },
    exportFieldGroups() {
      return groupRosterExportFields(this.exportFieldOptions)
    },
    rosterSelectableRows() {
      return (this.filteredAttendeeRows || []).filter((r) => this.rosterRowMemberId(r))
    },
    selectedRosterMemberIds() {
      const set = new Set()
      ;(this.selectedRosterRowKeys || []).forEach((key) => {
        const row = (this.filteredAttendeeRows || []).find((r) => String(r.rowKey || '') === String(key))
        const mid = row ? this.rosterRowMemberId(row) : 0
        if (mid) set.add(mid)
      })
      return [...set]
    },
    rosterAllSelectableChecked() {
      const rows = this.rosterSelectableRows
      if (!rows.length) return false
      return rows.every((r) => this.isRosterRowSelected(r))
    },
    filteredAttendeeRows() {
      const attendeeKw = String(this.regFilterAttendeeKeyword || '').toLowerCase()
      const submitter = String(this.regFilterSubmitter || '').toLowerCase()
      const fs = String(this.filterSourceLabel || '').trim()
      const fa = String(this.filterActivityStatus || '').trim()
      const fo = String(this.filterOrderStatusText || '').trim()
      const tagId = Number(this.filterMemberTagId || 0)
      const tagNeed =
        tagId > 0
          ? String((this.memberTags || []).find((t) => Number(t.id) === tagId)?.tagName || '').trim()
          : ''
      return (this.attendeeRows || []).filter((row) => {
        const n = String(row.attendeeName || '').toLowerCase()
        const m = String(row.attendeePhone || '').toLowerCase()
        const c = String(row.attendeeCompany || '').toLowerCase()
        const d = String(row.attendeeDuty || '').toLowerCase()
        const sub = String(row.submitterInfo || '').toLowerCase()
        if (attendeeKw) {
          const hit =
            n.includes(attendeeKw)
            || m.includes(attendeeKw)
            || c.includes(attendeeKw)
            || d.includes(attendeeKw)
          if (!hit) return false
        }
        if (submitter && !sub.includes(submitter)) return false
        if (fs) {
          const lbl = String(row.identityLabel || '').trim()
          if (lbl !== fs) return false
        }
        if (fa) {
          const act = String(row.activityStatusLabel || '').trim()
          if (act !== fa) return false
        }
        if (fo) {
          const ord = String(row.orderStatusText || '').trim()
          if (ord !== fo) return false
        }
        if (tagNeed) {
          const names = this.rosterRowTagNames(row)
          if (!names.includes(tagNeed)) return false
        }
        if (!this.rowInDateRange(row.submittedAt, this.filterSubmittedFrom, this.filterSubmittedTo)) return false
        if (!this.rowInDateRange(row.cancelledAt, this.filterCancelledFrom, this.filterCancelledTo)) return false
        return true
      })
    },
    rosterFilteredTotal() {
      return (this.filteredAttendeeRows || []).length
    },
    rosterRowOffset() {
      const size = Math.max(1, Number(this.rosterPageSize) || 20)
      const page = Math.max(1, Number(this.rosterPage) || 1)
      return (page - 1) * size
    },
    pagedAttendeeRows() {
      const rows = this.filteredAttendeeRows || []
      const size = Math.max(1, Number(this.rosterPageSize) || 20)
      const start = this.rosterRowOffset
      return rows.slice(start, start + size)
    },
    voucherAuditTitle() {
      if (this.voucherAuditMode === 'admin-refund') return '上传退费凭证'
      if (this.voucherAuditMode === 'admin-upload') return '帮他上传缴费凭证'
      if (this.voucherAuditViewSections.length > 1) return '缴费 / 退费凭证'
      if (this.voucherAuditViewSections.length === 1) return this.voucherAuditViewSections[0].title
      return this.voucherAuditCanReview ? '确认缴费' : '缴费凭证'
    },
    voucherAuditViewSections() {
      if (this.voucherAuditMode !== 'view') return []
      const r = this.voucherAuditRow
      if (!r) return []
      const payRel = this.voucherRel(r)
      const refundRel = this.refundVoucherRel(r)
      const u = this.orderStatusUpper(r)
      const showRefund =
        !!refundRel || u === 'REFUNDED' || u === 'REFUND_PENDING'
      const out = []
      if (payRel || !showRefund) {
        out.push({
          key: 'pay',
          title: '缴费凭证',
          url: payRel ? this.voucherAbs(payRel) : '',
          empty: '无缴费凭证',
        })
      }
      if (showRefund) {
        out.push({
          key: 'refund',
          title: '退费凭证',
          url: refundRel ? this.voucherAbs(refundRel) : '',
          empty: '无退费凭证',
        })
      }
      return out
    },
    voucherAuditMeta() {
      const snap = this.voucherAuditAttendeeSnapshot
      if (snap && (snap.attendeeName || snap.attendeePhone)) {
        return `${snap.attendeeName || '—'} · ${snap.attendeePhone || '—'}`
      }
      const r = this.voucherAuditRow
      if (!r) return ''
      return `${r.nickname || '—'} · ${r.mobile || '—'}`
    },
    voucherAuditActivityStatus() {
      const snap = this.voucherAuditAttendeeSnapshot
      if (snap && snap.activityStatusLabel) return String(snap.activityStatusLabel).trim()
      return '—'
    },
    voucherAuditOrderStatusZh() {
      const snap = this.voucherAuditAttendeeSnapshot
      if (snap && snap.orderStatusText) return String(snap.orderStatusText).trim()
      const r = this.voucherAuditRow
      return r ? this.orderStatusTextFromReg(r) : '—'
    },
    voucherAuditImg() {
      if (this.voucherAuditMode === 'admin-upload' || this.voucherAuditMode === 'admin-refund') {
        const u = String(this.adminUploadVoucherUrl || '').trim()
        return u ? this.voucherAbs(u) : ''
      }
      const r = this.voucherAuditRow
      if (!r) return ''
      const rel = this.refundVoucherRel(r) || this.voucherRel(r)
      return rel ? this.voucherAbs(rel) : ''
    },
    voucherAuditCanReview() {
      const r = this.voucherAuditRow
      if (!r) return false
      return this.orderStatusUpper(r) === 'REVIEWING' && !!this.regOrderId(r)
    },
    voucherAuditShowRejectVoucher() {
      const r = this.voucherAuditRow
      if (!r) return false
      return this.orderStatusUpper(r) === 'REVIEWING' && !!this.regOrderId(r)
    },
    submissionAuditMeta() {
      const row = this.submissionAuditTableRow
      if (!row) return ''
      const who = `${row.name || '—'} · ${row.phone || '—'}`
      const t = row.submittedAt ? ` · 报名时间 ${row.submittedAt}` : ''
      return `${who}${t}`
    },
    submissionAuditCollectFields() {
      const row = this.submissionAuditTableRow
      const block = row && row.block
      return this.collectFieldsForSignupAudit(block)
    },
    submissionAuditCanApprove() {
      const row = this.submissionAuditTableRow
      if (!row) return false
      if (this.regRowCancelled(this.signupPreviewRegRow)) return false
      if (this.signupBlockCancelled(row.block)) return false
      return this.previewRowShowApproveSubmission(row)
    },
    submissionAuditCanRejectSignup() {
      if (this.submissionAuditIsRejected) return false
      if (!this.canRejectSignupRow(this.signupPreviewRegRow)) return false
      const row = this.submissionAuditTableRow
      if (row && this.signupBlockCancelled(row.block)) return false
      const rev = row ? this.previewRowSubmissionReview(row) : ''
      if (rev === 'REJECTED' || rev === 'APPROVED') return false
      return true
    },
    submissionAuditIsRejected() {
      const row = this.submissionAuditTableRow
      if (row) {
        const rowRev = String(row.signupReviewStatus || row.signup_review_status || '')
          .trim()
          .toUpperCase()
        if (rowRev === 'REJECTED') return true
        const rev = this.previewRowSubmissionReview(row)
        if (rev === 'REJECTED') return true
        if (String(row.activityStatus || '').trim() === '该报名被驳回') return true
      }
      const ctx = this.regCtx(this.signupPreviewRegRow)
      const regRev = String(ctx.signupReviewStatus || ctx.signup_review_status || '')
        .trim()
        .toUpperCase()
      return regRev === 'REJECTED'
    },
    submissionAuditCanViewRejectReason() {
      return this.submissionAuditIsRejected
    },
    submissionAuditRejectReasonText() {
      const row = this.submissionAuditTableRow
      const pid =
        row && row.partitionSubmissionId != null ? String(row.partitionSubmissionId).trim() : ''
      const secs = this.signupPreviewSections || []
      const sec = secs.find((s) => {
        const sid = String((s.submissionId != null ? s.submissionId : s.submission_id) || '').trim()
        return pid && sid === pid
      })
      const fromSec =
        sec && String(sec.reviewNote != null ? sec.reviewNote : sec.review_note || '').trim()
      const block = row && row.block
      const fromBlock =
        block &&
        String(block.signupReviewNote || block.reviewNote || block.signup_review_note || '').trim()
      const fromRow =
        row && String(row.signupReviewNote || row.signup_review_note || '').trim()
      const t = fromSec || fromBlock || fromRow
      return t || '（未填写驳回原因）'
    },
    submissionAuditRejectReviewedAt() {
      const row = this.submissionAuditTableRow
      const pid =
        row && row.partitionSubmissionId != null ? String(row.partitionSubmissionId).trim() : ''
      const secs = this.signupPreviewSections || []
      const sec = secs.find((s) => {
        const sid = String((s.submissionId != null ? s.submissionId : s.submission_id) || '').trim()
        return pid && sid === pid
      })
      const fromSec =
        sec && String(sec.reviewedAt != null ? sec.reviewedAt : sec.reviewed_at || '').trim()
      const fromRow =
        row && String(row.signupReviewReviewedAt || row.signup_review_reviewed_at || '').trim()
      const ctx = this.regCtx(this.signupPreviewRegRow)
      const fromCtx = String(ctx.signupReviewReviewedAt || ctx.signup_review_reviewed_at || '').trim()
      const raw = String(fromSec || fromRow || fromCtx || '').trim()
      if (!raw) return ''
      return raw.length >= 16 ? raw.slice(0, 16).replace('T', ' ') : raw.replace('T', ' ')
    },
  },
  watch: {
    rosterFilteredTotal(total) {
      const tp = totalPagesFor(total, this.rosterPageSize)
      if (this.rosterPage > tp) {
        this.rosterPage = Math.max(1, tp)
      }
    },
    '$route.params.activityId'() {
      this.bootstrap()
    },
    '$route.query': {
      deep: true,
      handler() {
        if (this.activityId && this.attendeeRows.length) {
          this.applyRouteFocus()
        }
      },
    },
  },
  mounted() {
    this.canAct = hasAnyPermission(['content.manage', 'content.edit', '*'])
    this.bootstrap()
    this.startRosterPolling()
    document.addEventListener('visibilitychange', this.onRosterVisibilityChange)
  },
  beforeUnmount() {
    this.stopStaffQrScan()
    this.stopRosterPolling()
    document.removeEventListener('visibilitychange', this.onRosterVisibilityChange)
  },
  methods: {
    sourcePillClass(r) {
      const t = String(r.sourceLabel || r.source_label || '')
      if (t.includes('会员') && !t.includes('非')) return 'pill-green'
      if (t.includes('非会员')) return 'pill-gray'
      return 'pill-blue'
    },
    identityPillClass(label) {
      return this.identityStatusClass(label)
    },
    statusPillClass(label) {
      const t = String(label || '').trim()
      if (!t || t === '—') return STATUS_PILL_CLASS_BY_LABEL['—']
      return STATUS_PILL_CLASS_BY_LABEL[t] || 'status-pill--unknown'
    },
    identityStatusClass(label) {
      return this.statusPillClass(label)
    },
    activityStatusClass(label) {
      return this.statusPillClass(label)
    },
    orderStatusClass(text) {
      return this.statusPillClass(text)
    },
    rosterCancelBtnClass(row) {
      const label = this.attendeeRowCancelActionLabel(row)
      if (label === '审核取消报名') return 'btn-act-cancel-audit'
      if (label === '查看取消驳回') return 'btn-act-cancel-rejected'
      return 'btn-act-cancel-reason'
    },
    rosterCheckinSigBtnClass(row) {
      return this.attendeeRowCheckinSignatureUrl(row) ? 'btn-act-signature-view' : 'btn-act-signature-upload'
    },
    orderStatusTextFromReg(r) {
      return this.previewDialogOrderStatusText(r)
    },
    rowInDateRange(cellText, fromDate, toDate) {
      const from = String(fromDate || '').trim()
      const to = String(toDate || '').trim()
      if (!from && !to) return true
      const raw = String(cellText || '').trim()
      if (!raw || raw === '—') return false
      const day = raw.replace('T', ' ').slice(0, 10)
      if (from && day < from) return false
      if (to && day > to) return false
      return true
    },
    regCtx(row) {
      if (!row) return {}
      return row.regContext || row.reg_context || {}
    },
    /** 该行参会人是否承担活动费（每人独立订单，有费用且未驳回） */
    attendeeRowIsActivityFeeBearer(row) {
      if (!row) return false
      const raw = row.personBlock || row.person_block
      if (raw && (raw.activityFeeBearer === true || raw.activity_fee_bearer === true)) return true
      const ctx = this.regCtx(row)
      if (ctx.activityFeeBearer === true || ctx.activity_fee_bearer === true) return true
      return false
    },
    attendeePersonBlock(row) {
      const raw = row && (row.personBlock || row.person_block)
      if (!raw || typeof raw !== 'object') return {}
      const mapped = this.mapPeopleBlocks([raw])
      return mapped[0] || {}
    },
    rosterDateTimeDisplay(v) {
      return formatDateTimeForDisplay(v)
    },
    /** 活动状态为「已签到」时，在标签下方展示签到时间 */
    attendeeRowCheckinAtDisplay(row) {
      if (!row || String(row.activityStatusLabel || '').trim() !== '已签到') return ''
      const block = this.attendeePersonBlock(row)
      const raw = String(
        (block && block.slotCheckinAt) ||
          (row.personBlock && row.personBlock.slotCheckinAt) ||
          (row.person_block && row.person_block.slot_checkin_at) ||
          '',
      ).trim()
      if (!raw) return ''
      const formatted = formatDateTimeForDisplay(raw)
      return formatted === '—' ? '' : formatted
    },
    attendeeRowCheckinSignatureUrl(row) {
      const block = this.attendeePersonBlock(row)
      const raw = String(
        (block && (block.slotCheckinSignatureUrl || block.slot_checkin_signature_url)) ||
          (row.personBlock && (row.personBlock.slotCheckinSignatureUrl || row.personBlock.slot_checkin_signature_url)) ||
          (row.person_block && (row.person_block.slot_checkin_signature_url || row.person_block.slotCheckinSignatureUrl)) ||
          '',
      ).trim()
      return raw
    },
    attendeeRowShowCheckinSignatureAction(row) {
      return row && String(row.activityStatusLabel || '').trim() === '已签到'
    },
    attendeeRowSlotIndex(row) {
      const block = this.attendeePersonBlock(row)
      let slot = Number(block.slotIndex != null ? block.slotIndex : block.slot_index)
      if (!Number.isFinite(slot) || slot < 0) slot = 0
      return slot
    },
    openCheckinSignatureDlg(row) {
      const rel = this.attendeeRowCheckinSignatureUrl(row)
      const url = rel ? this.voucherAbs(rel) : ''
      const name = row && row.attendeeName ? String(row.attendeeName) : '参会人'
      const at = this.attendeeRowCheckinAtDisplay(row)
      this.checkinSignatureDlg = {
        visible: true,
        previewUrl: url,
        uploadUrl: '',
        meta: at ? `${name} · 签到 ${at}` : name,
        err: '',
        busy: false,
        row,
      }
    },
    closeCheckinSignatureDlg() {
      this.checkinSignatureDlg = {
        visible: false,
        previewUrl: '',
        uploadUrl: '',
        meta: '',
        err: '',
        busy: false,
        row: null,
      }
    },
    pickAdminCheckinSignatureFile() {
      const el = this.$refs.adminCheckinSignatureFileInput
      if (el && typeof el.click === 'function') el.click()
    },
    async onAdminCheckinSignatureFilePicked(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      this.checkinSignatureDlg.busy = true
      this.checkinSignatureDlg.err = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && data.url) || '').trim()
        if (!url) throw new Error('上传失败')
        this.checkinSignatureDlg.uploadUrl = url
        this.checkinSignatureDlg.previewUrl = this.voucherAbs(url)
      } catch (err) {
        this.checkinSignatureDlg.err = (err && err.message) || '上传失败'
      } finally {
        this.checkinSignatureDlg.busy = false
      }
    },
    async saveAdminCheckinSignature() {
      const dlg = this.checkinSignatureDlg
      const row = dlg && dlg.row
      if (!this.canAct || !this.activityId || !row) return
      const sid = this.attendeeRowSignupSubmissionId(row)
      const slot = this.attendeeRowSlotIndex(row)
      const imageUrl = String((dlg && dlg.uploadUrl) || '').trim()
      if (!sid) {
        dlg.err = '缺少报名提交单 ID'
        return
      }
      if (!imageUrl) {
        dlg.err = '请先选择并上传签名图片'
        return
      }
      dlg.busy = true
      dlg.err = ''
      try {
        await assignAdminAttendeeCheckinSignature(this.activityId, sid, slot, {
          imageUrl,
          checkinAt: this.attendeeRowCheckinAtDisplay(row) || '',
        })
        this.okMsg = '已保存电子签名'
        this.closeCheckinSignatureDlg()
        await this.loadRegs()
      } catch (err) {
        dlg.err = (err && err.message) || '保存失败'
      } finally {
        dlg.busy = false
      }
    },
    attendeeAuditRow(row) {
      const block = this.attendeePersonBlock(row)
      const pid =
        row.signupFormSubmissionId != null
          ? String(row.signupFormSubmissionId)
          : block.partitionSubmissionId != null
            ? String(block.partitionSubmissionId).trim()
            : ''
      const merged = {
        ...block,
        partitionSubmissionId: pid,
        partitionNote: row.partitionNote || block.partitionNote || '',
        partitionSubmittedAt: row.submittedAt || block.partitionSubmittedAt || '',
      }
      return {
        block: merged,
        name: row.attendeeName || '—',
        phone: row.attendeePhone || '—',
        company: row.attendeeCompany || '—',
        duty: row.attendeeDuty || '—',
        identityLabel: row.identityLabel || '—',
        activityStatus: row.activityStatusLabel || '—',
        orderStatusText: row.orderStatusText || '—',
        cancelledAt: row.cancelledAt || '—',
        showCancelReasonBtn: this.attendeeRowShowCancelReasonBtn(row),
        partitionSubmissionId: pid,
        submissionId: Number(pid) || 0,
        slotIndex: merged.slotIndex != null ? Number(merged.slotIndex) : 0,
        partitionNote: row.partitionNote || '',
        submittedAt: row.submittedAt || '',
        signupReviewStatus: String(
          (block.signupReviewStatus ||
            block.status ||
            this.regCtx(row).signupReviewStatus ||
            this.regCtx(row).signup_review_status) ||
            '',
        )
          .trim()
          .toUpperCase(),
        signupReviewNote: String(
          (block.signupReviewNote ||
            block.reviewNote ||
            this.regCtx(row).signupReviewNote ||
            this.regCtx(row).signup_review_note) ||
            '',
        ).trim(),
        signupReviewReviewedAt: String(
          (block.signupReviewReviewedAt ||
            block.reviewedAt ||
            this.regCtx(row).signupReviewReviewedAt ||
            this.regCtx(row).signup_review_reviewed_at) ||
            '',
        ).trim(),
        idx: 0,
      }
    },
    closeSubmissionAuditRejectPanels() {
      this.submissionAuditShowRejectNote = false
      this.submissionAuditShowRejectReasonView = false
    },
    bindAttendeeRegContext(row) {
      const ctx = this.regCtx(row)
      this.signupPreviewRegRow = ctx && Object.keys(ctx).length ? ctx : null
      const sid =
        row.signupFormSubmissionId != null
          ? String(row.signupFormSubmissionId)
          : ''
      const rev = String(ctx.signupReviewStatus || ctx.signup_review_status || '').trim()
      const reviewNote = String(ctx.signupReviewNote || ctx.signup_review_note || '').trim()
      const reviewedAt = String(ctx.signupReviewReviewedAt || ctx.signup_review_reviewed_at || '').trim()
      this.signupPreviewSections = sid
        ? [
            {
              submissionId: sid,
              submittedAt: row.submittedAt || '',
              reviewStatus: rev,
              reviewNote,
              reviewedAt,
              people: [],
            },
          ]
        : []
    },
    attendeeRowSignupSubmissionId(row) {
      const block = this.attendeePersonBlock(row)
      const v =
        row && row.signupFormSubmissionId != null
          ? row.signupFormSubmissionId
          : row && row.signup_form_submission_id != null
            ? row.signup_form_submission_id
            : block.partitionSubmissionId != null
              ? block.partitionSubmissionId
              : block.submissionId != null
                ? block.submissionId
                : null
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    goEditSignupPayload(row) {
      const sid = this.attendeeRowSignupSubmissionId(row)
      if (!this.activityId || !sid) {
        window.alert('无报名提交记录，无法编辑')
        return
      }
      this.closeSignupSubmissionAudit()
      const block = row.personBlock || row.person_block || {}
      let slot = Number(block.slotIndex != null ? block.slotIndex : block.slot_index)
      if (!Number.isFinite(slot) || slot < 0) slot = 0
      this.signupEditDlg = {
        visible: true,
        submissionId: sid,
        initialSlotIndex: slot,
      }
    },
    closeSignupEditDlg() {
      this.signupEditDlg = { visible: false, submissionId: 0, initialSlotIndex: 0 }
    },
    openProxySignupDlg() {
      this.proxySignupDlg = { visible: true }
    },
    closeProxySignupDlg() {
      this.proxySignupDlg = { visible: false }
    },
    async onProxySignupSaved() {
      this.okMsg = '会务代报已提交'
      await this.loadRegs({ silent: true })
    },
    async onSignupEditSaved() {
      this.okMsg = '报名信息已保存'
      await this.loadRegs()
    },
    openSignupSubmissionAuditFromAttendee(row) {
      if (!row) return
      this.bindAttendeeRegContext(row)
      this.openSignupSubmissionAudit(this.attendeeAuditRow(row))
    },
    attendeeRowCancelActionLabel(row) {
      const block = row.personBlock || row.person_block
      const st = String(block.cancelRequestStatus || block.cancel_request_status || '').toUpperCase()
      if (st === 'PENDING') return '审核取消报名'
      if (st === 'REJECTED') return '查看取消驳回'
      return '查看取消原因'
    },
    attendeeRowShowCancelReasonBtn(row) {
      const block = row.personBlock || row.person_block
      const st = String(block.cancelRequestStatus || block.cancel_request_status || '').toUpperCase()
      if (st === 'PENDING' || st === 'REJECTED') return true
      if (this.signupBlockCancelled(block)) return true
      const ca = String(row.cancelledAt || '').trim()
      return !!ca && ca !== '—'
    },
    attendeeRowDomId(row) {
      const sid = this.attendeeRowSignupSubmissionId(row) || 0
      const slot = this.attendeeSlotIndex(row)
      return `roster-row-${sid}-${slot}`
    },
    isFocusedAttendeeRow(row) {
      const key = String(this.focusRowKey || '').trim()
      if (!key) return false
      const sid = this.attendeeRowSignupSubmissionId(row)
      const slot = this.attendeeSlotIndex(row)
      return key === `${sid}:${slot}`
    },
    startRosterPolling() {
      if (this.rosterPollTimer != null) return
      this.rosterPollTimer = window.setInterval(() => {
        if (document.visibilityState === 'visible' && this.activityId) {
          void this.loadRegs({ silent: true })
        }
      }, 30000)
    },
    stopRosterPolling() {
      if (this.rosterPollTimer != null) {
        window.clearInterval(this.rosterPollTimer)
        this.rosterPollTimer = null
      }
    },
    onRosterVisibilityChange() {
      if (document.visibilityState === 'visible' && this.activityId) {
        void this.loadRegs({ silent: true })
      }
    },
    applyRouteFocus() {
      const q = this.$route.query || {}
      const sid = Number(q.focusSubmissionId)
      const slotRaw = Number(q.focusSlot)
      const action = String(q.focusAction || '').trim()
      if (!Number.isFinite(sid) || sid <= 0) return
      const slot = Number.isFinite(slotRaw) && slotRaw >= 0 ? slotRaw : 0
      this.focusRowKey = `${sid}:${slot}`
      const rows = this.filteredAttendeeRows || []
      const idx = rows.findIndex(
        (r) => this.attendeeRowSignupSubmissionId(r) === sid && this.attendeeSlotIndex(r) === slot,
      )
      if (idx >= 0) {
        const size = Math.max(1, Number(this.rosterPageSize) || 20)
        this.rosterPage = Math.floor(idx / size) + 1
      }
      this.$nextTick(() => {
        const el = document.getElementById(`roster-row-${sid}-${slot}`)
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        const row = rows.find(
          (r) => this.attendeeRowSignupSubmissionId(r) === sid && this.attendeeSlotIndex(r) === slot,
        )
        if (row) {
          if (action === 'cancelAudit') this.openSignupCancelReasonDlg(row)
          else if (action === 'signupAudit') this.openSignupSubmissionAuditFromAttendee(row)
          else if (action === 'feeAudit') {
            const ctx = this.regCtx(row)
            if (this.attendeeRowShowConfirmPay(row)) this.onAttendeeRowConfirmPay(row)
            else if (this.attendeeRowShowVoucherView(row)) this.openVoucherAudit(ctx, row)
            else if (this.attendeeRowShowAdminUploadVoucher(row)) this.onAdminUploadVoucherClick(row)
          }
        }
        if (Object.keys(q).length) {
          this.$router.replace({ path: this.$route.path, query: {} }).catch(() => {})
        }
      })
    },
    attendeeRowShowMarkRefundedBtn(row) {
      if (this.attendeeRowShowAdminUploadRefundVoucher(row)) return false
      if (!this.attendeeRowIsActivityFeeBearer(row)) return false
      const block = row.personBlock || row.person_block
      if (!this.signupBlockCancelled(block) && !this.attendeeRowShowCancelReasonBtn(row)) return false
      const r = this.regCtx(row)
      if (!r) return false
      const rev = String(
        (block && (block.signupReviewStatus || block.status)) ||
          r.signupReviewStatus ||
          r.signup_review_status ||
          '',
      )
        .trim()
        .toUpperCase()
      if (rev !== 'APPROVED') return false
      const oid = this.regOrderId(r)
      if (!oid) return false
      const u = this.orderStatusUpper(r)
      if (u === 'REFUNDED') return false
      const orderZh = String((row && row.orderStatusText) || '').trim()
      if (orderZh !== '待退回' && u !== 'REFUND_PENDING') return false
      return u === 'PAID' || u === 'COMPLETED' || u === 'REFUND_PENDING'
    },
    /** 订单待退回：会务上传退费凭证并确认退款（不要求报名已取消） */
    attendeeRowShowAdminUploadRefundVoucher(row) {
      if (!this.attendeeRowIsActivityFeeBearer(row)) return false
      const r = this.regCtx(row)
      if (!r || !this.regOrderId(r)) return false
      const u = this.orderStatusUpper(r)
      if (u !== 'REFUND_PENDING') return false
      const orderZh = String((row && row.orderStatusText) || '').trim()
      return orderZh === '待退回' || u === 'REFUND_PENDING'
    },
    attendeeRowPersonSignupApproved(row) {
      const block = this.attendeePersonBlock(row)
      const ctx = this.regCtx(row)
      const rev = String(
        (block && (block.signupReviewStatus || block.status)) ||
          ctx.signupReviewStatus ||
          ctx.signup_review_status ||
          '',
      )
        .trim()
        .toUpperCase()
      return rev === 'APPROVED'
    },
    attendeeSignupApprovedForFee(row) {
      if (!this.attendeeRowPersonSignupApproved(row)) return false
      const act = String((row && row.activityStatusLabel) || '').trim()
      return act !== '该报名被驳回' && act !== '已取消'
    },
    /**
     * 订单状态为待支付/缴费驳回即可代传凭证（不要求已线上支付；无 orderId 时上传前会自动补建订单）。
     */
    attendeeRowShowAdminUploadVoucher(row) {
      if (!this.attendeeRowIsActivityFeeBearer(row)) return false
      const orderZh = String((row && row.orderStatusText) || '').trim()
      const r = this.regCtx(row)
      if (
        (orderZh === '审核缴费信息中' || orderZh === '待支付') &&
        this.attendeeRowOnlinePaidAwaitingVoucher(row) &&
        !this.voucherRel(r)
      ) {
        return this.attendeeRowPersonSignupApproved(row)
      }
      if (orderZh === '审核缴费信息中') {
        return false
      }
      if (this.attendeeRowShowConfirmPay(row)) return false
      if (orderZh !== '待支付' && orderZh !== '该缴费被驳回') return false
      if (r && this.voucherRel(r)) return false
      const act = String((row && row.activityStatusLabel) || '').trim()
      if (act === '审核报名信息中' || act === '该报名被驳回' || act === '已取消') return false
      if (orderZh === '该缴费被驳回') {
        return this.attendeeRowPersonSignupApproved(row)
      }
      return true
    },
    attendeeSignupChannelLabel(row) {
      const r = this.regCtx(row)
      const ch = r && (r.signupChannel != null ? r.signupChannel : r.signup_channel)
      return ch ? String(ch).trim() : ''
    },
    adminUploadVoucherBtnLabel(row) {
      const ch = this.attendeeSignupChannelLabel(row)
      if (ch === '临时扫码报名' || ch === '现场代报') return '补充缴费凭证'
      return '帮他上传缴费凭证'
    },
    attendeeRowOnlinePaidAwaitingVoucher(row) {
      const r = this.regCtx(row)
      return !!(
        r &&
        (r.onlinePaidAwaitingVoucher === true || r.online_paid_awaiting_voucher === true)
      )
    },
    /** 会员已传凭证（线下/代传）待会务确认；线上已付未传凭证时不展示（改展示「帮他上传缴费凭证」）。 */
    attendeeRowShowConfirmPay(row) {
      if (!this.attendeeRowIsActivityFeeBearer(row)) return false
      if (!this.canAct) return false
      const r = this.regCtx(row)
      if (!r || this.regRowCancelled(r)) return false
      if (this.attendeeRowShowCancelReasonBtn(row)) return false
      const oid = this.regOrderId(r)
      if (!oid) return false
      const act = String(row.activityStatusLabel || '').trim()
      if (act === '审核报名信息中' || act === '该报名被驳回' || act === '已取消') return false
      if (this.attendeeRowOnlinePaidAwaitingVoucher(row) && !this.voucherRel(r)) return false
      const orderZh = String(row.orderStatusText || '').trim()
      if (orderZh === '审核缴费信息中') return true
      return this.orderStatusUpper(r) === 'REVIEWING'
    },
    attendeeRowShowVoucherReview(row) {
      const r = this.regCtx(row)
      return !!r && this.orderStatusUpper(r) === 'REVIEWING' && !!this.regOrderId(r)
    },
    /**
     * 订单已进入缴费/退费凭证环节（已提交或需查看凭证）。
     * 支付前取消（订单「已取消」、无凭证）不在此列；与活动状态是否「已取消」无关。
     */
    attendeeRowOrderInvolvesVoucher(row) {
      const r = this.regCtx(row)
      if (!r || !this.regOrderId(r)) return false
      if (this.voucherRel(r) || this.refundVoucherRel(r)) return true
      const orderZh = String((row && row.orderStatusText) || '').trim()
      const u = this.orderStatusUpper(r)
      if (
        u === 'PAID' ||
        u === 'COMPLETED' ||
        u === 'REVIEWING' ||
        u === 'REFUNDED' ||
        u === 'REFUND_PENDING' ||
        u === 'REJECTED'
      ) {
        return true
      }
      return (
        orderZh === '已支付' ||
        orderZh === '审核缴费信息中' ||
        orderZh === '该缴费被驳回' ||
        orderZh === '待退回' ||
        orderZh === '已退回'
      )
    },
    attendeeRowShowVoucherView(row) {
      if (!this.attendeeRowIsActivityFeeBearer(row)) return false
      if (!this.attendeeRowOrderInvolvesVoucher(row)) return false
      const r = this.regCtx(row)
      if (!r) return false
      if (this.attendeeRowShowVoucherReview(row) || this.attendeeRowShowConfirmPay(row)) return false
      if (this.attendeeRowShowAdminUploadVoucher(row)) return false
      return true
    },
    onAttendeeRowConfirmPay(row) {
      const r = this.regCtx(row)
      if (!this.canAct || !r || !this.attendeeRowShowConfirmPay(row)) return
      this.openVoucherAudit(r, row)
    },
    async submitMarkActivityFeeRefundedForAttendee(row) {
      if (!this.canAct || !this.activityId) return
      const r = this.regCtx(row)
      const oid = this.regOrderId(r)
      const mid = this.regMemberId(r)
      if (!r || !oid) return
      if (!await adminConfirm('确认已在场外完成活动费退回？确定后订单状态将标记为「已退回」。')) return
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await markAdminActivityFeeOrderRefunded(this.activityId, oid, { memberId: mid })
        this.okMsg = '订单状态已更新为已退回'
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '标记失败'
      }
    },
    signupSubmissionId(r) {
      const v = r.signupFormSubmissionId != null ? r.signupFormSubmissionId : r.signup_form_submission_id
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    /** 会员行：即使列表未带回 submissionId，也可走按会员拉取报名信息的接口 */
    regMemberId(r) {
      const v = r.memberId != null ? r.memberId : r.member_id
      const n = Number(v)
      if (Number.isFinite(n) && n > 0) return n
      const ctx = r.regContext || r.reg_context
      if (ctx && ctx.memberId != null) {
        const m = Number(ctx.memberId)
        if (Number.isFinite(m) && m > 0) return m
      }
      return null
    },
    rosterRowMemberId(row) {
      return this.regMemberId(row)
    },
    rosterRowTagNames(row) {
      const names = row && (row.tagNames || row.tag_names)
      if (!Array.isArray(names)) return []
      return names.map((x) => String(x).trim()).filter(Boolean)
    },
    enterRosterSelectMode() {
      this.rosterSelectMode = true
      this.selectedRosterRowKeys = []
    },
    exitRosterSelectMode() {
      this.rosterSelectMode = false
      this.selectedRosterRowKeys = []
    },
    isRosterRowSelected(row) {
      const key = String(row && row.rowKey ? row.rowKey : '')
      return key && (this.selectedRosterRowKeys || []).includes(key)
    },
    toggleRosterRowSelected(row) {
      const key = String(row && row.rowKey ? row.rowKey : '')
      if (!key || !this.rosterRowMemberId(row)) return
      const list = [...(this.selectedRosterRowKeys || [])]
      const i = list.indexOf(key)
      if (i >= 0) list.splice(i, 1)
      else list.push(key)
      this.selectedRosterRowKeys = list
    },
    toggleRosterSelectAll() {
      if (this.rosterAllSelectableChecked) {
        this.selectedRosterRowKeys = []
        return
      }
      this.selectedRosterRowKeys = this.rosterSelectableRows.map((r) => String(r.rowKey || '')).filter(Boolean)
    },
    async loadMemberTags() {
      try {
        const rows = (await getAdminMemberTags()) || []
        this.memberTags = Array.isArray(rows) ? rows : []
      } catch {
        this.memberTags = []
      }
    },
    openRosterTagDialog(row) {
      const ids = row ? [this.rosterRowMemberId(row)].filter(Boolean) : [...this.selectedRosterMemberIds]
      if (!ids.length) {
        this.errorMsg = row ? '该行无会员账号，无法打标签' : '请勾选要打标签的会员行'
        return
      }
      let selected = []
      if (row) {
        const names = new Set(this.rosterRowTagNames(row))
        selected = (this.memberTags || []).filter((t) => names.has(String(t.tagName).trim())).map((t) => t.id)
      }
      this.tagDlg = {
        visible: true,
        memberIds: ids,
        selectedTagIds: selected,
        replaceExisting: !!row,
        busy: false,
      }
    },
    closeRosterTagDialog() {
      this.tagDlg.visible = false
      this.tagDlg.busy = false
    },
    async promptNewMemberTag() {
      const name = await adminPrompt('请输入新标签名称（不可重复）')
      if (!name) return
      try {
        this.tagDlg.busy = true
        const created = await createAdminMemberTag(String(name).trim())
        await this.loadMemberTags()
        if (created && created.id && !this.tagDlg.selectedTagIds.includes(created.id)) {
          this.tagDlg.selectedTagIds = [...this.tagDlg.selectedTagIds, created.id]
        }
      } catch (e) {
        this.errorMsg = e.message || '新增标签失败'
      } finally {
        this.tagDlg.busy = false
      }
    },
    async submitRosterTagDialog() {
      if (!this.tagDlg.selectedTagIds.length) return
      try {
        this.tagDlg.busy = true
        await assignAdminMemberTags({
          memberIds: this.tagDlg.memberIds,
          tagIds: this.tagDlg.selectedTagIds.map((id) => Number(id)),
          replaceExisting: this.tagDlg.replaceExisting,
        })
        this.closeRosterTagDialog()
        this.exitRosterSelectMode()
        this.okMsg = '标签已保存'
        await this.loadRegs({ silent: true })
      } catch (e) {
        this.errorMsg = e.message || '打标签失败'
      } finally {
        this.tagDlg.busy = false
      }
    },
    buildRosterExportFilter() {
      return {
        identity: this.filterSourceLabel,
        attendeeKeyword: this.regFilterAttendeeKeyword,
        submitter: this.regFilterSubmitter,
        activityStatus: this.filterActivityStatus,
        orderStatus: this.filterOrderStatusText,
        tagId: this.filterMemberTagId,
        submittedFrom: this.filterSubmittedFrom,
        submittedTo: this.filterSubmittedTo,
        cancelledFrom: this.filterCancelledFrom,
        cancelledTo: this.filterCancelledTo,
      }
    },
    async openRosterExportDialog() {
      if (!this.activityId) return
      this.exportDlg = {
        visible: true,
        loading: true,
        busy: false,
        selectedKeys: [],
      }
      this.errorMsg = ''
      try {
        let rows = null
        try {
          rows = (await fetchAdminActivityRosterExportFields(this.activityId)) || []
        } catch (e) {
          const msg = String((e && e.message) || '')
          if (!/404/.test(msg)) throw e
          rows = null
        }
        if (!Array.isArray(rows) || !rows.length) {
          let detail = this.activityDetail
          if (!detail) {
            detail = await getAdminCmsActivity(this.activityId)
            this.activityDetail = detail && typeof detail === 'object' ? detail : null
          }
          rows = buildRosterExportFieldOptions(detail)
        }
        this.exportFieldOptions = Array.isArray(rows) ? rows : buildRosterExportFieldOptions(this.activityDetail)
        this.exportDlg.selectedKeys = resolveRosterExportSelectedKeys(this.activityId, this.exportFieldOptions)
      } catch (e) {
        this.errorMsg = e.message || '加载导出字段失败'
        this.closeRosterExportDialog()
      } finally {
        this.exportDlg.loading = false
      }
    },
    closeRosterExportDialog() {
      this.exportDlg.visible = false
      this.exportDlg.busy = false
    },
    selectAllExportFields() {
      this.exportDlg.selectedKeys = (this.exportFieldOptions || []).map((o) => o.key)
    },
    clearExportFields() {
      this.exportDlg.selectedKeys = []
    },
    resetExportFieldsDefault() {
      this.exportDlg.selectedKeys = (this.exportFieldOptions || [])
        .filter((o) => o.defaultSelected)
        .map((o) => o.key)
    },
    async submitRosterExport() {
      if (!this.activityId || !this.exportDlg.selectedKeys.length) return
      this.exportingRoster = true
      this.exportDlg.busy = true
      this.errorMsg = ''
      this.okMsg = ''
      try {
        persistRosterExportFieldKeys(this.activityId, this.exportDlg.selectedKeys)
        await downloadAdminActivityRosterExportXlsx(this.activityId, {
          fields: this.exportDlg.selectedKeys,
          filter: this.buildRosterExportFilter(),
        })
        this.okMsg = 'Excel 已下载'
        this.closeRosterExportDialog()
      } catch (e) {
        const msg = String((e && e.message) || '导出失败')
        this.errorMsg = /404/.test(msg)
          ? '导出接口未就绪，请重启后端 huiyuanguanlihouduan1（8081）后再试'
          : msg
      } finally {
        this.exportingRoster = false
        this.exportDlg.busy = false
      }
    },
    isMemberRegRow(r) {
      return String(r.sourceType || r.source_type || '').toUpperCase() === 'MEMBER'
    },
    /** 名单行是否处于已取消（仍可能保留历史报名表，供后台查看） */
    regRowCancelled(r) {
      if (!r) return false
      const u = String(r.statusCode || r.status_code || '').trim().toUpperCase()
      const codes = new Set([
        'CANCELLED',
        'CANCELED',
        'WITHDRAWN',
        'USER_CANCELLED',
        'SIGNUP_CANCELLED',
        'REG_CANCELLED',
        'ABANDONED',
      ])
      if (codes.has(u)) return true
      const lab = this.regStatusLabel(r)
      return lab.includes('取消')
    },
    canViewSignupInfo(r) {
      if (this.signupSubmissionId(r)) return true
      return this.isMemberRegRow(r) && !!this.regMemberId(r)
    },
    attendeeDisplayCount(r) {
      const n = Number(r.attendeeCount != null ? r.attendeeCount : r.attendee_count)
      return Number.isFinite(n) && n > 0 ? n : 1
    },
    regRowHasActions(r) {
      if (this.canApproveSignupRow(r) || this.canViewSignupInfo(r)) return true
      if (this.orderStatusUpper(r) === 'REVIEWING' && this.regOrderId(r)) return true
      return !!this.voucherRel(r)
    },
    canRejectSignupRow(r) {
      if (!this.isMemberRegRow(r) || !this.canViewSignupInfo(r) || this.regRowCancelled(r)) return false
      const u = String(r.statusCode || r.status_code || '').trim().toUpperCase()
      if (u === 'REJECTED' || u === 'SIGNUP_REJECTED' || u === 'CANCELLED' || u === 'ENDED') return false
      return true
    },
    canApproveSignupRow(r) {
      if (!this.isMemberRegRow(r) || !this.canViewSignupInfo(r) || this.regRowCancelled(r)) return false
      if (this.regOrderId(r)) return false
      const u = String(r.statusCode || r.status_code || '').trim().toUpperCase()
      const canFin = r.canFinalizeOrder === true || r.can_finalize_order === true
      if (canFin) {
        return (
          u === 'SIGNUP_REVIEWING' ||
          u === 'REVIEWING' ||
          u === 'PENDING_REVIEW' ||
          u === 'UNDER_REVIEW' ||
          !u
        )
      }
      const hasFlowCounts =
        r.pendingSubmissionCount != null ||
        r.pending_submission_count != null ||
        r.approvedSubmissionCount != null ||
        r.approved_submission_count != null
      if (!hasFlowCounts) {
        return u === 'REVIEWING' || u === 'PENDING_REVIEW' || u === 'UNDER_REVIEW'
      }
      return false
    },
    feeDisplayText(r) {
      return String(r.activityFeeStatus || r.activity_fee_status || '—').trim() || '—'
    },
    regCancelledAtDisplay(r) {
      const v = r && (r.cancelledAt != null ? r.cancelledAt : r.cancelled_at)
      return formatDateTimeForDisplay(v)
    },
    attendeeSignupCancelledAtDisplay(row) {
      return formatDateTimeForDisplay(row && row.cancelledAt)
    },
    orderCancelledAtDisplay(row) {
      const ctx = this.regCtx(row)
      const v = ctx.orderCancelledAt != null ? ctx.orderCancelledAt : ctx.order_cancelled_at
      return formatDateTimeForDisplay(v)
    },
    refundedAtDisplay(row) {
      const ctx = this.regCtx(row)
      const v = ctx.refundedAt != null ? ctx.refundedAt : ctx.refunded_at
      return formatDateTimeForDisplay(v)
    },
    orderStatusSubline(row) {
      const t = String((row && row.orderStatusText) || '').trim()
      const ctx = this.regCtx(row)
      if (this.attendeeRowOnlinePaidAwaitingVoucher(row)) {
        return '线上缴费未上传凭证'
      }
      if (t === '待支付') {
        if (this.attendeeRowIsActivityFeeBearer(row) && this.attendeeRowPersonSignupApproved(row)) {
          return '可小程序线上支付或线下上传凭证'
        }
      }
      if (t === '该缴费被驳回' && this.attendeeRowIsActivityFeeBearer(row)) {
        return '可重新支付或会务代传凭证'
      }
      const u = this.orderStatusUpper(ctx)
      if (u === 'REFUNDED' || t === '已退回') {
        const at = this.refundedAtDisplay(row)
        return at !== '—' ? `退回时间：${at}` : ''
      }
      if (t === '待退回' || t === '已取消' || u === 'CANCELLED' || u === 'REFUND_PENDING') {
        const at = this.orderCancelledAtDisplay(row)
        return at !== '—' ? `取消订单：${at}` : ''
      }
      return ''
    },
    /**
     * 弹窗表格「取消时间」：优先参会人块上的 cancelledAt；缺失时回退到登记行 cancelled_at（整单取消常见）；
     * 仍缺则在同一次提交的 people 里取任意一条非空取消时间（兼容旧数据只写了一处）。
     */
    resolveSignupPreviewRowCancelledAt(block, reg) {
      const bc = this.signupBlockCancelled(block)
      const regOff = this.regRowCancelled(reg)
      if (!bc && !regOff) return '—'
      const fromBlock = this.signupBlockCancelledAt(block)
      if (fromBlock !== '—') return fromBlock
      const fromReg = this.regCancelledAtDisplay(reg)
      if (fromReg !== '—') return fromReg
      const pid = String((block && block.partitionSubmissionId) || '').trim()
      const secs = this.signupPreviewSections || []
      const sec = secs.find((s) => String((s.submissionId != null ? s.submissionId : s.submission_id) || '').trim() === pid)
      if (!sec || !Array.isArray(sec.people)) return '—'
      for (const p of sec.people) {
        const t = this.signupBlockCancelledAt(p)
        if (t !== '—') return t
      }
      return '—'
    },
    voucherRel(r) {
      const u = r.activityFeeVoucherUrl || r.activity_fee_voucher_url
      const s = String(u || '').trim()
      if (!s || /^mock:\/\//i.test(s)) return ''
      return s
    },
    refundVoucherRel(r) {
      const u = r.activityFeeRefundVoucherUrl || r.activity_fee_refund_voucher_url
      return String(u || '').trim()
    },
    feeStatusPillClass(r) {
      const u = this.orderStatusUpper(r)
      if (u === 'PAID' || u === 'COMPLETED') return 'pill-green'
      if (u === 'REFUNDED') return 'pill-gray'
      if (u === 'REFUND_PENDING') return 'pill-warn'
      if (u === 'REVIEWING') return 'pill-warn'
      if (u === 'PENDING') return 'pill-info'
      if (u === 'CANCEL_REQUESTED') return 'pill-amber'
      if (u === 'REJECTED') return 'pill-red'
      if (u === 'CANCELLED') return 'pill-gray'
      const t = this.feeDisplayText(r)
      if (t === '无订单') return 'pill-amber'
      if (t === '—' || !t) return 'pill-gray'
      return 'pill-gray'
    },
    regStatusPillClass(r) {
      const label = this.regStatusLabel(r)
      if (label.includes('已报名') || label.includes('待参加') || label.includes('已完成')) return 'pill-green'
      if (label.includes('表单已提交')) return 'pill-info'
      if (label.includes('审核中')) return 'pill-warn'
      if (label.includes('待支付')) return 'pill-info'
      if (label.includes('活动费待审核')) return 'pill-warn'
      if (label.includes('取消订单待审核')) return 'pill-amber'
      if (label.includes('驳回')) return 'pill-red'
      if (label.includes('取消') || label.includes('结束')) return 'pill-gray'
      return 'pill-gray'
    },
    notifyAdminErr(msg) {
      const t = String(msg || '').trim()
      if (!t) return
      this.errorMsg = t
      try {
        window.alert(t)
      } catch {
        /* ignore */
      }
    },
    onAdminUploadVoucherClick(attendeeRow) {
      if (!this.canAct) {
        this.notifyAdminErr('当前账号无内容管理权限，无法代传缴费凭证')
        return
      }
      void this.openAdminUploadVoucher(attendeeRow)
    },
    async openAdminUploadVoucher(attendeeRow) {
      if (!attendeeRow) return
      this.errorMsg = ''
      this.okMsg = ''
      this.voucherAuditDialogMsg = ''
      const existingCtx = this.regCtx(attendeeRow)
      const hasOrder = !!this.regOrderId(existingCtx)
      if (!hasOrder) {
        this.voucherApproveBusy = true
      }
      try {
        const ctx = hasOrder ? existingCtx : await this.resolveAttendeeFeeOrderContext(attendeeRow)
        if (!ctx || !this.regOrderId(ctx)) {
          if (!this.errorMsg) {
            this.notifyAdminErr('未能打开代传凭证：请先在「审核报名信息」中通过该参会人')
          } else {
            this.notifyAdminErr(this.errorMsg)
          }
          return
        }
        this.voucherAuditMode = 'admin-upload'
        this.adminUploadVoucherUrl = ''
        this.openVoucherAudit(ctx, attendeeRow)
      } catch (e) {
        this.notifyAdminErr((e && e.message) || '打开代传凭证失败')
      } finally {
        this.voucherApproveBusy = false
      }
    },
    /** 名单行尚无 activityFeeOrderId 时，按提交单+槽位补建活动费订单后返回最新 regContext */
    async resolveAttendeeFeeOrderContext(attendeeRow) {
      let r = this.regCtx(attendeeRow)
      if (this.regOrderId(r)) return r
      const sid = this.attendeeRowSignupSubmissionId(attendeeRow)
      if (!this.activityId || !sid) {
        this.errorMsg = '缺少报名提交单，无法代传凭证'
        return null
      }
      const block = this.attendeePersonBlock(attendeeRow)
      let slot = Number(block.slotIndex != null ? block.slotIndex : block.slot_index)
      if (!Number.isFinite(slot) || slot < 0) slot = 0
      try {
        await approveAdminActivitySignupSubmission(this.activityId, sid, { slotIndex: slot })
        await this.loadRegs()
        const key = attendeeRow && attendeeRow.rowKey
        const updated =
          key && (this.attendeeRows || []).find((x) => x.rowKey === key)
            ? (this.attendeeRows || []).find((x) => x.rowKey === key)
            : attendeeRow
        r = this.regCtx(updated)
        if (!this.regOrderId(r)) {
          const act = String((updated && updated.activityStatusLabel) || '').trim()
          if (act === '审核报名信息中') {
            this.errorMsg = '请先点击「审核报名信息」并通过该参会人，再代传缴费凭证'
          } else {
            this.errorMsg =
              '未能生成活动费订单：参会人手机号可能未注册会员，请刷新名单后重试；仍失败请联系技术支持'
          }
          return null
        }
        return r
      } catch (e) {
        this.errorMsg = e.message || '生成活动费订单失败'
        return null
      }
    },
    openAdminUploadRefundVoucher(attendeeRow) {
      const r = this.regCtx(attendeeRow)
      if (!r || !this.attendeeRowShowAdminUploadRefundVoucher(attendeeRow)) return
      this.voucherAuditMode = 'admin-refund'
      this.adminUploadVoucherUrl = ''
      this.voucherAuditDialogMsg = ''
      this.openVoucherAudit(r, attendeeRow)
    },
    pickAdminVoucherFile() {
      const el = this.$refs.adminVoucherFileInput
      if (el && typeof el.click === 'function') el.click()
    },
    pickAdminRefundVoucherFile() {
      const el = this.$refs.adminRefundVoucherFileInput
      if (el && typeof el.click === 'function') el.click()
    },
    async onAdminVoucherFilePicked(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      this.voucherApproveBusy = true
      this.errorMsg = ''
      this.voucherAuditDialogMsg = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && data.url) || '').trim()
        if (!url) throw new Error('未获取到图片地址')
        this.adminUploadVoucherUrl = url
      } catch (err) {
        const msg = err.message || '上传失败'
        this.errorMsg = msg
        this.voucherAuditDialogMsg = msg
      } finally {
        this.voucherApproveBusy = false
      }
    },
    async confirmAdminUploadAndApprove() {
      if (!this.canAct || !this.activityId) return
      let r = this.voucherAuditRow
      let oid = this.regOrderId(r)
      if (!oid && this.voucherAuditAttendeeSnapshot) {
        r = await this.resolveAttendeeFeeOrderContext(this.voucherAuditAttendeeSnapshot)
        oid = r ? this.regOrderId(r) : null
        if (r) this.voucherAuditRow = r
      }
      if (!oid) return
      const url = String(this.adminUploadVoucherUrl || '').trim()
      if (!url) {
        this.errorMsg = '请先选择并上传缴费凭证图片'
        return
      }
      this.voucherApproveBusy = true
      this.errorMsg = ''
      try {
        await submitAdminActivityFeeOfflineVoucher(this.activityId, oid, {
          voucherUrl: url,
          memberId: this.regMemberId(r),
        })
        await approveAdminMemberOrderOfflinePay(oid, {})
        this.okMsg = '已上传凭证并确认缴费通过'
        this.closeVoucherAudit()
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '操作失败'
      } finally {
        this.voucherApproveBusy = false
      }
    },
    async confirmAdminRefund() {
      const r = this.voucherAuditRow
      const oid = this.regOrderId(r)
      this.voucherAuditDialogMsg = ''
      if (!this.canAct) {
        this.voucherAuditDialogMsg = '暂无操作权限'
        return
      }
      if (!this.activityId) {
        this.voucherAuditDialogMsg = '活动 ID 无效'
        return
      }
      if (!oid) {
        this.voucherAuditDialogMsg = '未找到关联订单，请关闭弹窗后刷新名单重试'
        return
      }
      const url = String(this.adminUploadVoucherUrl || '').trim()
      if (!url) {
        this.voucherAuditDialogMsg = '请先点击「选择凭证图片」上传退费凭证'
        return
      }
      this.voucherApproveBusy = true
      this.errorMsg = ''
      try {
        // 代报时订单在参会人账号，regContext.memberId 为代报人，勿传 memberId 以免后端校验失败
        await markAdminActivityFeeOrderRefunded(this.activityId, oid, { voucherUrl: url })
        this.okMsg = '已上传退费凭证并确认退回'
        this.closeVoucherAudit()
        await this.loadRegs()
      } catch (e) {
        const msg = e.message || '操作失败'
        this.errorMsg = msg
        this.voucherAuditDialogMsg = msg
      } finally {
        this.voucherApproveBusy = false
      }
    },
    openVoucherAudit(r, attendeeRow = null) {
      const adminUpload =
        this.voucherAuditMode === 'admin-upload' || this.voucherAuditMode === 'admin-refund'
      if (!adminUpload && attendeeRow && !this.attendeeRowIsActivityFeeBearer(attendeeRow)) {
        this.notifyAdminErr('该行不承担活动费，无法查看凭证')
        return
      }
      if (!adminUpload) {
        this.voucherAuditMode = 'view'
      }
      if (!r || typeof r !== 'object') {
        this.notifyAdminErr('报名信息上下文缺失，请刷新名单后重试')
        return
      }
      this.voucherAuditRow = r
      this.voucherAuditAttendeeSnapshot =
        attendeeRow && typeof attendeeRow === 'object' ? attendeeRow : null
      this.voucherApproveBusy = false
      this.voucherAuditDialogMsg = ''
      this.showVoucherAudit = true
    },
    closeVoucherAudit() {
      this.showVoucherAudit = false
      this.voucherAuditMode = 'view'
      this.voucherAuditRow = null
      this.voucherAuditAttendeeSnapshot = null
      this.voucherApproveBusy = false
      this.adminUploadVoucherUrl = ''
      this.voucherAuditDialogMsg = ''
    },
    closeSignupSubmissionAudit() {
      this.signupSubmissionAuditVisible = false
      this.submissionAuditTableRow = null
      this.submissionAuditRejectNote = ''
      this.submissionAuditRejectBusy = false
      this.submissionAuditShowRejectReasonView = false
      this.submissionAuditShowRejectNote = false
    },
    openSignupSubmissionAudit(row) {
      if (!row) return
      this.closeSignupEditDlg()
      this.submissionAuditTableRow = row
      this.submissionAuditRejectNote = ''
      this.submissionAuditShowRejectNote = false
      this.signupSubmissionAuditVisible = true
    },
    collectFieldsForSignupAudit(block) {
      const fields = Array.isArray(block && block.fields) ? block.fields : []
      const out = []
      for (let i = 0; i < fields.length; i += 1) {
        const f = fields[i]
        const lab = String((f && f.label) || '').trim()
        if (!lab || lab.startsWith('__')) continue
        if (/姓名|名字|真实姓名/.test(lab)) continue
        if (/手机|移动电话|联系电话|电话|mobile|phone/i.test(lab)) continue
        if (/单位名称|公司名称|^单位$/.test(lab)) continue
        if (/单位职务|职务|职位/.test(lab)) continue
        const val = f && f.value != null ? String(f.value).trim() : ''
        out.push({ label: lab || '—', value: val || '—' })
      }
      return out
    },
    async submitSubmissionAuditApprove() {
      const row = this.submissionAuditTableRow
      if (!row) return
      const ok = await this.onPreviewRowApproveSubmission(row)
      if (ok) this.closeSignupSubmissionAudit()
    },
    auditRowSlotIndex(row) {
      const block = row && row.block
      if (!block) return 0
      const n = Number(block.slotIndex != null ? block.slotIndex : block.slot_index)
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
    },
    async submitSubmissionAuditReject() {
      if (!this.canAct || !this.activityId) return
      const row = this.submissionAuditTableRow
      const sid = row && row.partitionSubmissionId != null ? String(row.partitionSubmissionId).trim() : ''
      if (!sid) return
      this.submissionAuditRejectBusy = true
      this.errorMsg = ''
      try {
        await rejectAdminActivitySignupSubmission(this.activityId, sid, {
          note: String(this.submissionAuditRejectNote || '').trim(),
          slotIndex: this.auditRowSlotIndex(row),
        })
        this.okMsg = '已驳回报名'
        this.closeSignupSubmissionAudit()
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '驳回失败'
      } finally {
        this.submissionAuditRejectBusy = false
      }
    },
    signupBlockCancelled(block) {
      if (!block || typeof block !== 'object') return false
      if (block.cancelled === true || block.canceled === true) return true
      const st = String(block.status != null ? block.status : block.state != null ? block.state : '')
        .trim()
        .toUpperCase()
      return st === 'CANCELLED' || st === 'CANCELED'
    },
    signupBlockCancelledAt(block) {
      const v = block && (block.cancelledAt != null ? block.cancelledAt : block.cancelled_at)
      return formatDateTimeForDisplay(v)
    },
    normalizeDlgMobile(phone) {
      return String(phone || '').replace(/\D/g, '')
    },
    openSignupCancelReasonDlg(row) {
      const b = (row && (row.personBlock || row.person_block)) || (row && row.block) || {}
      const reqSt = String(b.cancelRequestStatus || b.cancel_request_status || '').toUpperCase()
      const pending = reqSt === 'PENDING'
      const rejected = reqSt === 'REJECTED'
      const reason = String(
        (pending || rejected
          ? b.cancelRequestReason || b.cancel_request_reason
          : b.cancelReason || b.cancel_reason) || '',
      ).trim()
      const rejectNote = String(b.cancelRequestRejectNote || b.cancel_request_reject_note || '').trim()
      const requestBy = pending || rejected
        ? String(b.cancelRequestBy || b.cancel_request_by || '').trim()
        : String(b.cancelledBy || b.cancelled_by || '').trim()
      const requestOperatorMobile = String(
        b.cancelRequestOperatorMobile || b.cancel_request_operator_mobile || '',
      ).trim()
      const attendeeName = String((row && row.attendeeName) || '').trim()
      const attendeePhone = String((row && row.attendeePhone) || '').trim()
      const attendeeLine =
        attendeeName && attendeePhone
          ? `${attendeeName} · ${attendeePhone}`
          : attendeeName || attendeePhone || '—'
      const rowMobile = this.normalizeDlgMobile(attendeePhone)
      const opMobile = this.normalizeDlgMobile(requestOperatorMobile)
      let mismatchWarn = ''
      if (pending && rowMobile && opMobile && rowMobile !== opMobile) {
        mismatchWarn =
          '申请人与本行参会人手机号不一致：该条取消申请可能因历史错槽位挂在当前行。请驳回本行申请，并请申请人从「我的报名→详情」重新提交；勿对错误行点「通过」。'
      } else if (pending && rowMobile && !opMobile && requestBy && attendeePhone && !attendeePhone.includes(requestBy)) {
        mismatchWarn =
          '申请人展示名与本行参会人信息不一致，请核对后再审核；若为历史错槽数据，请驳回后由申请人重新提交。'
      }
      const requestAtRaw = b.cancelRequestAt || b.cancel_request_at
      const rejectAtRaw = b.cancelRequestRejectAt || b.cancel_request_reject_at
      const requestAtDisplay = formatDateTimeForDisplay(requestAtRaw || '')
      const rejectAtDisplay = formatDateTimeForDisplay(rejectAtRaw || '')
      const atRaw = pending || rejected ? requestAtRaw : b.cancelledAt || b.cancelled_at
      const atDisplay = formatDateTimeForDisplay(atRaw || '')
      const sid = Number(
        row && row.signupFormSubmissionId != null
          ? row.signupFormSubmissionId
          : b.partitionSubmissionId || 0,
      )
      const slot = Number(b.slotIndex != null ? b.slotIndex : b.slot_index != null ? b.slot_index : 0)
      let meta = ''
      if (pending) meta = '会员申请取消报名，请审核。'
      else if (rejected) meta = '取消报名申请已驳回，以下为申请记录。'
      this.signupCancelReasonDlg = {
        visible: true,
        pending: !!pending,
        rejected: !!rejected,
        busy: false,
        showRejectInput: false,
        rejectNote: pending ? '' : rejectNote,
        submissionId: sid,
        slotIndex: slot,
        meta,
        attendeeLine,
        requestOperatorMobile,
        mismatchWarn,
        cancelReason: reason,
        requestBy,
        requestAt: requestAtDisplay === '—' ? '' : requestAtDisplay,
        rejectAt: rejectAtDisplay === '—' ? '' : rejectAtDisplay,
        cancelledBy: pending || rejected ? '' : requestBy,
        cancelledAt: pending || rejected ? '' : atDisplay === '—' ? '' : atDisplay,
      }
    },
    closeSignupCancelReasonDlg() {
      this.signupCancelReasonDlg = {
        visible: false,
        pending: false,
        busy: false,
        showRejectInput: false,
        rejectNote: '',
        submissionId: 0,
        slotIndex: 0,
        meta: '',
        cancelReason: '',
        requestBy: '',
        requestAt: '',
        cancelledBy: '',
        cancelledAt: '',
        attendeeLine: '',
        requestOperatorMobile: '',
        mismatchWarn: '',
      }
    },
    async submitApproveSignupCancelRequest() {
      const d = this.signupCancelReasonDlg
      if (!d.pending || !this.activityId || !d.submissionId) return
      if (!await adminConfirm('确认通过取消报名申请？通过后将按当前缴费/签到状态执行取消或待退回。')) return
      this.errorMsg = ''
      this.okMsg = ''
      d.busy = true
      try {
        await approveAdminSignupCancelRequest(this.activityId, d.submissionId, d.slotIndex)
        this.okMsg = '已通过取消报名申请'
        this.closeSignupCancelReasonDlg()
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = (e && e.message) || '操作失败'
      } finally {
        d.busy = false
      }
    },
    async submitRejectSignupCancelRequest() {
      const d = this.signupCancelReasonDlg
      const note = String(d.rejectNote || '').trim()
      if (!note) {
        this.errorMsg = '请填写驳回原因'
        return
      }
      if (!d.pending || !this.activityId || !d.submissionId) return
      this.errorMsg = ''
      this.okMsg = ''
      d.busy = true
      try {
        await rejectAdminSignupCancelRequest(this.activityId, d.submissionId, d.slotIndex, { note })
        this.okMsg = '已驳回取消报名申请'
        this.closeSignupCancelReasonDlg()
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = (e && e.message) || '操作失败'
      } finally {
        d.busy = false
      }
    },
    /** 仅首行展示，避免多行重复；条件：名单已取消 + 活动费曾支付 + 订单尚未标记已退回 */
    previewRowShowsMarkRefundedBtn(row) {
      if (!row || row.idx !== 1) return false
      const r = this.signupPreviewRegRow
      if (!r || !this.regRowCancelled(r)) return false
      const oid = this.regOrderId(r)
      if (!oid) return false
      const u = this.orderStatusUpper(r)
      if (u === 'REFUNDED') return false
      return u === 'PAID' || u === 'COMPLETED' || u === 'REFUND_PENDING'
    },
    async submitMarkActivityFeeRefunded() {
      if (!this.canAct || !this.activityId) return
      const r = this.signupPreviewRegRow
      const oid = this.regOrderId(r)
      const mid = this.regMemberId(r)
      if (!r || !oid) return
      if (!await adminConfirm('确认已在场外完成活动费退回？确定后订单状态将标记为「已退回」。')) return
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await markAdminActivityFeeOrderRefunded(this.activityId, oid, { memberId: mid })
        this.okMsg = '订单状态已更新为已退回'
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '标记失败'
      }
    },
    mainTableOrderStatusText(r) {
      const feeRaw = r.effectiveSignupFeeCent != null ? r.effectiveSignupFeeCent : r.effective_signup_fee_cent
      const feeCent = Number.isFinite(Number(feeRaw))
        ? Math.max(0, Number(feeRaw))
        : Math.max(0, Number(this.activitySignupFeeCent || 0))
      if (feeCent <= 0) return '无订单'
      if (this.regRowCancelled(r)) {
        const oid = this.regOrderId(r)
        if (!oid) return '—'
        const u = this.orderStatusUpper(r)
        if (u === 'REFUNDED') return '已退回'
        if (u === 'PAID' || u === 'COMPLETED' || u === 'REFUND_PENDING') return '待退回'
        return '—'
      }
      const rev = String(r.signupReviewStatus || r.signup_review_status || '').trim().toUpperCase()
      if (rev === 'REJECTED') return '—'
      if (rev && rev !== 'APPROVED') return '—'
      const u = this.orderStatusUpper(r)
      if (u === 'COMPLETED' || u === 'PAID') return '无订单'
      if (u === 'PENDING' || u === 'REVIEWING') return '有订单'
      if (rev === 'APPROVED' && !this.regOrderId(r)) return '有订单'
      return '无订单'
    },
    previewRowOrderStatusText(reg, row) {
      const subRev = row ? this.previewRowSubmissionReview(row) : ''
      if (subRev === 'PENDING' || subRev === 'NEW' || subRev === 'SUBMITTED' || subRev === '') {
        return '—'
      }
      if (subRev === 'REJECTED') return '—'
      return this.previewDialogOrderStatusText(reg)
    },
    previewDialogOrderStatusText(r) {
      if (!r) return '—'
      const feeRaw = r.effectiveSignupFeeCent != null ? r.effectiveSignupFeeCent : r.effective_signup_fee_cent
      const feeCent = Number.isFinite(Number(feeRaw))
        ? Math.max(0, Number(feeRaw))
        : Math.max(0, Number(this.activitySignupFeeCent || 0))
      const regRev = String(r.signupReviewStatus || r.signup_review_status || '').trim().toUpperCase()
      if (regRev === 'REJECTED' || regRev === 'CANCELLED') return '—'
      if (regRev && regRev !== 'APPROVED') return '—'
      const oid = this.regOrderId(r)
      const u = this.orderStatusUpper(r)
      const paid = u === 'PAID' || u === 'COMPLETED'
      if (feeCent <= 0) return '—'
      if (!oid) return '—'
      if (this.regRowCancelled(r)) {
        if (u === 'REFUNDED') return '已退回'
        if (paid || u === 'REFUND_PENDING') return '待退回'
        if (u === 'REJECTED') return '该缴费被驳回'
        if (u === 'CANCELLED' || u === 'CANCEL_REQUESTED' || u === 'CLOSED') return '已取消'
        return '—'
      }
      if (u === 'REJECTED') return '该缴费被驳回'
      if (u === 'CANCELLED' || u === 'CANCEL_REQUESTED' || u === 'CLOSED') return '已取消'
      if (u === 'PENDING') return '待支付'
      if (u === 'REVIEWING') return '审核缴费信息中'
      if (paid) return '已支付'
      if (u === 'REFUNDED') return '已退回'
      if (u === 'REFUND_PENDING') return '待退回'
      return '—'
    },
    previewRowSubmissionReview(row) {
      const pid = String((row && row.partitionSubmissionId) || '').trim()
      const secs = this.signupPreviewSections || []
      const sec = secs.find((s) => {
        const sid = String((s.submissionId != null ? s.submissionId : s.submission_id) || '').trim()
        return sid === pid
      })
      return sec
        ? String(sec.reviewStatus != null ? sec.reviewStatus : sec.review_status || '')
        .trim()
        .toUpperCase()
        : ''
    },
    previewRowShowApproveSubmission(row) {
      if (!this.canAct || !this.activityId) return false
      if (this.regRowCancelled(this.signupPreviewRegRow)) return false
      if (row && this.signupBlockCancelled(row.block)) return false
      const sid = row && row.partitionSubmissionId != null ? String(row.partitionSubmissionId).trim() : ''
      if (!sid) return false
      const rs = this.previewRowSubmissionReview(row)
      if (rs === 'APPROVED' || rs === 'REJECTED') return false
      return true
    },
    previewRowShowConfirmPay() {
      if (!this.canAct || this.isSignupPreviewRegRowCancelled()) return false
      const r = this.signupPreviewRegRow
      const oid = this.regOrderId(r)
      if (!oid) return false
      const u = this.orderStatusUpper(r)
      return u === 'PENDING' || u === 'REVIEWING'
    },
    async onPreviewRowApproveSubmission(row) {
      if (!this.canAct || !this.activityId) return false
      const sid = row && row.partitionSubmissionId != null ? String(row.partitionSubmissionId).trim() : ''
      if (!sid) return false
      this.errorMsg = ''
      this.okMsg = ''
      this.signupPreviewSubmissionBusy = sid
      try {
        await approveAdminActivitySignupSubmission(this.activityId, sid, {
          slotIndex: this.auditRowSlotIndex(row),
        })
        this.okMsg = '报名信息已审核通过'
        await this.loadRegs()
        return true
      } catch (e) {
        this.errorMsg = e.message || '通过失败'
        return false
      } finally {
        this.signupPreviewSubmissionBusy = ''
      }
    },
    onPreviewRowConfirmPay() {
      const r = this.signupPreviewRegRow
      if (!this.canAct || !r || !this.previewRowShowConfirmPay()) return
      this.openVoucherAudit(r)
    },
    async confirmApproveSignup(r) {
      if (!this.canAct || !this.activityId) return
      const mid = this.regMemberId(r)
      if (!mid) return
      const ok = await adminConfirm(
        '确认为该会员生成本场活动费相关订单/待支付状态？（单次提交的报名请在「查看报名信息」→「审核报名信息」中操作「报名信息通过」。）',
      )
      if (!ok) return
      this.errorMsg = ''
      try {
        await approveAdminActivityMemberSignup(this.activityId, { memberId: mid })
        this.okMsg = '已生成待支付活动费订单'
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '操作失败（请确认后端已实现接口）'
      }
    },
    formatPartitionSubmittedAt(raw) {
      if (raw == null || raw === '') return ''
      const s = String(raw).trim()
      if (!s) return ''
      return s.replace('T', ' ')
    },
    signupPreviewFieldByLabel(block, regexList) {
      const fields = Array.isArray(block && block.fields) ? block.fields : []
      for (let fi = 0; fi < fields.length; fi += 1) {
        const f = fields[fi]
        const lab = String((f && f.label) || '').trim()
        for (let ri = 0; ri < regexList.length; ri += 1) {
          if (regexList[ri].test(lab)) {
            const v = f.value == null ? '' : String(f.value).trim()
            return v || '—'
          }
        }
      }
      return '—'
    },
    signupPreviewDisplayName(block) {
      const v = this.signupPreviewFieldByLabel(block, [/姓名|名字/])
      if (v !== '—') return v
      const h = String((block && block.heading) || '').trim()
      return h || '—'
    },
    signupPreviewCheckinColsForBlock(block) {
      if (this.isSignupPreviewRegRowCancelled()) {
        return { status: this.signupPreviewSlotCheckinLine(block), time: '—', hold: false }
      }
      const r = this.signupPreviewRegRow
      if (r && !this.adminRowFeeSettledForCheckinPreview(r)) {
        return { status: this.signupPreviewSlotCheckinLine(block), time: '—', hold: true }
      }
      const at = String((block && block.slotCheckinAt) || '').trim()
      if (block && block.slotCheckedIn && at) {
        const tfmt = at.replace('T', ' ').slice(0, 19)
        return { status: '已签到', time: tfmt, hold: false }
      }
      return { status: this.signupPreviewSlotCheckinLine(block), time: '—', hold: false }
    },
    submissionReviewForBlock(block) {
      const pid = block && block.partitionSubmissionId != null ? String(block.partitionSubmissionId).trim() : ''
      const secs = this.signupPreviewSections || []
      if (pid) {
        const sec = secs.find((s) => String(s.submissionId).trim() === pid)
        return sec ? String(sec.reviewStatus || '').trim().toUpperCase() : ''
      }
      if (secs.length === 1) return String(secs[0].reviewStatus || '').trim().toUpperCase()
      return ''
    },
    /** 会务已通过、活动费未结清时的活动状态（与名单列一致） */
    adminActivityStatusWhenFeeNotSettledPreview(r) {
      if (!r) return '待支付'
      const orderZh = String(this.previewDialogOrderStatusText(r) || '').trim()
      const u = this.orderStatusUpper(r)
      if (orderZh === '审核缴费信息中' || u === 'REVIEWING') return '审核缴费信息中'
      if (orderZh === '该缴费被驳回' || u === 'REJECTED') return '该缴费被驳回'
      return '待支付'
    },
    /** 活动状态（会务审核 + 缴费进度 + 签到） */
    signupPreviewActivityStatusForBlock(block) {
      const r = this.signupPreviewRegRow
      if (this.regRowCancelled(r)) return '已取消'
      if (block && block.cancelled) return '已取消'
      const h = block && block.heading != null ? String(block.heading) : ''
      if (h.includes('人员已清空')) return '已取消'

      const subRev = this.submissionReviewForBlock(block)
      if (subRev === 'REJECTED') return '该报名被驳回'
      if (subRev === 'PENDING' || subRev === 'NEW' || subRev === 'SUBMITTED' || !subRev) {
        return '审核报名信息中'
      }
      if (subRev !== 'APPROVED') return '审核报名信息中'

      if (r && !this.adminRowFeeSettledForCheckinPreview(r)) {
        return this.adminActivityStatusWhenFeeNotSettledPreview(r)
      }
      if (block && block.slotCheckedIn) return '已签到'
      const ck = this.signupPreviewCheckinColsForBlock(block)
      if (ck.status === '已签到') return '已签到'
      return '待签到'
    },
    mapPeopleBlocks(peopleRaw) {
      const people = Array.isArray(peopleRaw) ? peopleRaw : []
      return people.map((b) => ({
        heading: (b && b.heading) || '',
        fields: Array.isArray(b && b.fields)
          ? b.fields.map((x) => ({
              label: (x && x.label) || '—',
              value: x == null || x.value == null ? '' : String(x.value),
            }))
          : [],
        slotIndex: b && b.slotIndex != null ? Number(b.slotIndex) : null,
        slotCheckedIn: !!(b && b.slotCheckedIn),
        slotCheckinAt: b && b.slotCheckinAt != null ? String(b.slotCheckinAt) : '',
        slotCheckinSignatureUrl:
          b && (b.slotCheckinSignatureUrl || b.slot_checkin_signature_url)
            ? String(b.slotCheckinSignatureUrl || b.slot_checkin_signature_url)
            : '',
        cancelled: (() => {
          if (!b || typeof b !== 'object') return false
          if (b.cancelled === true || b.canceled === true || b.isCancelled === true || b.isCanceled === true) return true
          const st0 = String((b.status != null ? b.status : b.state) || '')
            .trim()
            .toUpperCase()
          return st0 === 'CANCELLED' || st0 === 'CANCELED'
        })(),
        status: b && (b.status != null ? b.status : b.state),
        state: b && b.state != null ? b.state : b.status,
        cancelReason: String((b && (b.cancelReason || b.cancel_reason)) || '').trim(),
        cancelledAt: String((b && (b.cancelledAt || b.cancelled_at)) || '').trim(),
        cancelledBy: String((b && (b.cancelledBy || b.cancelled_by)) || '').trim(),
        sourceHint: (b && (b.sourceHint || b.source_hint)) || '',
        statusLabel: b && b.statusLabel != null ? String(b.statusLabel) : '',
        signupReviewStatus:
          b && b.signupReviewStatus != null ? String(b.signupReviewStatus) : b && b.status != null ? String(b.status) : '',
        signupReviewNote: String((b && (b.signupReviewNote || b.reviewNote || b.signup_review_note)) || '').trim(),
        reviewNote: String((b && (b.reviewNote || b.review_note)) || '').trim(),
        signupReviewReviewedAt: String(
          (b && (b.signupReviewReviewedAt || b.reviewedAt || b.signup_review_reviewed_at)) || '',
        ).trim(),
        reviewedAt: String((b && (b.reviewedAt || b.reviewed_at)) || '').trim(),
        cancelRequestStatus: String((b && (b.cancelRequestStatus || b.cancel_request_status)) || '').trim(),
        cancelRequestReason: String((b && (b.cancelRequestReason || b.cancel_request_reason)) || '').trim(),
        cancelRequestAt: String((b && (b.cancelRequestAt || b.cancel_request_at)) || '').trim(),
        cancelRequestBy: String((b && (b.cancelRequestBy || b.cancel_request_by)) || '').trim(),
        cancelRequestRejectNote: String(
          (b && (b.cancelRequestRejectNote || b.cancel_request_reject_note)) || '',
        ).trim(),
        cancelRequestOperatorMobile: String(
          (b && (b.cancelRequestOperatorMobile || b.cancel_request_operator_mobile)) || '',
        ).trim(),
        partitionSubmissionId:
          b && b.partitionSubmissionId != null ? String(b.partitionSubmissionId) : '',
      }))
    },
    /** 活动费已结清才在签到列展示「未签到/已签到」；未结清时签到列为短标签（待支付/凭证审等） */
    adminRowFeeSettledForCheckinPreview(r) {
      if (!r) return true
      const t = this.feeDisplayText(r)
      if (t === '—' || !t) return true
      if (t.includes('已支付')) return true
      if (this.orderStatusUpper(r) === 'PAID') return true
      return false
    },
    adminCheckinBlockedByFeeLabel(r) {
      if (!r || this.adminRowFeeSettledForCheckinPreview(r)) return ''
      const t = this.feeDisplayText(r)
      if (t.includes('凭证') || t.includes('审核')) return '凭证审'
      if (t.includes('驳回')) return '费驳回'
      if (t.includes('取消')) return '费取消'
      if (t.includes('待支付') || t.includes('无订单')) return '待支付'
      return '未结清'
    },
    signupPreviewSlotCheckinFeeHold() {
      if (this.isSignupPreviewRegRowCancelled()) return false
      const r = this.signupPreviewRegRow
      return !!(r && !this.adminRowFeeSettledForCheckinPreview(r))
    },
    /** 每条参会人卡片的分项签到状态（短标签；时间见「签到时间」列） */
    signupPreviewSlotCheckinLine(block) {
      if (this.isSignupPreviewRegRowCancelled()) return '已取消'
      const r = this.signupPreviewRegRow
      if (r && !this.adminRowFeeSettledForCheckinPreview(r)) {
        return this.adminCheckinBlockedByFeeLabel(r)
      }
      if (!block) return '未签到'
      const at = String(block.slotCheckinAt || '').trim()
      if (block.slotCheckedIn && at) return '已签到'
      return '未签到'
    },
    isSignupPreviewRegRowCancelled() {
      return this.regRowCancelled(this.signupPreviewRegRow)
    },
    async fromVoucherApprove() {
      const r = this.voucherAuditRow
      const oid = this.regOrderId(r)
      if (!this.canAct || !oid) {
      this.closeVoucherAudit()
          return
        }
      this.errorMsg = ''
      this.voucherApproveBusy = true
      try {
        await approveAdminMemberOrderOfflinePay(oid, {})
        this.okMsg = '缴费审核已通过'
        this.closeVoucherAudit()
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '操作失败'
      } finally {
        this.voucherApproveBusy = false
      }
    },
    fromVoucherReject() {
      const r = this.voucherAuditRow
      this.closeVoucherAudit()
      if (r) this.openReject(r)
    },
    async bootstrap() {
      const raw = this.$route.params.activityId
      const id = Number(raw)
      this.activityId = Number.isFinite(id) && id > 0 ? id : null
      this.errorMsg = ''
      this.okMsg = ''
      if (!this.activityId) {
        this.errorMsg = '无效的活动 ID'
        return
      }
      const tab = String(this.$route.query.tab || '').toLowerCase()
      if (tab === 'form') {
        await this.$router.replace({ path: this.$route.path, query: {} })
      }
      await this.loadActivityMeta()
      await this.loadMemberTags()
      await this.loadRegs()
    },
    async loadActivityMeta() {
      try {
        const row = await getAdminCmsActivity(this.activityId)
        this.activityDetail = row && typeof row === 'object' ? row : null
        this.activityTitle =
          row && (row.title || row.name) ? String(row.title || row.name) : ''
        const fee = row && (row.signupFeeCent != null ? row.signupFeeCent : row.signup_fee_cent)
        this.activitySignupFeeCent = Number.isFinite(Number(fee)) ? Math.max(0, Number(fee)) : 0
        const badgeOn = row && (row.enableBadgePrint != null ? row.enableBadgePrint : row.enable_badge_print)
        this.activityEnableBadgePrint = badgeOn === true || badgeOn === 1 || badgeOn === '1'
        const mode = row && (row.checkinMode != null ? row.checkinMode : row.checkin_mode)
        this.activityCheckinMode = mode ? String(mode).trim().toUpperCase() : 'STAFF_SCAN_ATTENDEE'
        const tid = row && (row.badgeTemplateId != null ? row.badgeTemplateId : row.badge_template_id)
        const n = Number(tid)
        this.activityBadgeTemplateId = Number.isFinite(n) && n > 0 ? n : null
      } catch {
        this.activityDetail = null
        this.activityTitle = ''
        this.activityCheckinMode = 'STAFF_SCAN_ATTENDEE'
        this.activityEnableBadgePrint = false
        this.activityBadgeTemplateId = null
      }
    },
    goEditBadgeTemplate() {
      const q = { tab: 'badge' }
      const tid =
        (this.nameBadgeDlg.card && this.nameBadgeDlg.card.templateId) || this.activityBadgeTemplateId
      if (tid) q.editId = String(tid)
      this.$router.push({ path: '/member/templates', query: q })
    },
    attendeeSlotIndex(row) {
      const pb = (row && (row.personBlock || row.person_block)) || {}
      const n = Number(pb.idx != null ? pb.idx : pb.slotIndex != null ? pb.slotIndex : pb.slot_index)
      return Number.isFinite(n) && n >= 0 ? n : 0
    },
    async openAttendeeNameBadge(row) {
      if (!this.activityId) return
      const ctx = this.regCtx(row)
      const mid = this.regMemberId(ctx)
      if (!mid) {
        this.errorMsg = '无法识别报名会员，暂无法生成胸卡'
        return
      }
      const slot = this.attendeeSlotIndex(row)
      const sid = this.attendeeRowSignupSubmissionId(row)
      this.nameBadgeDlg.meta = `${row.attendeeName || '—'} · ${row.attendeeDuty || '—'}`
      this.nameBadgeDlg.visible = true
      this.nameBadgeDlg.card = { available: false, message: '加载中…' }
      try {
        this.nameBadgeDlg.card = await getAdminAttendeeNameBadge(this.activityId, {
          memberId: mid,
          slotIndex: slot,
          submissionId: sid,
        })
      } catch (e) {
        this.nameBadgeDlg.card = { available: false, message: e.message || '加载失败' }
      }
    },
    closeNameBadgeDlg() {
      this.nameBadgeDlg.visible = false
      this.nameBadgeDlg.card = null
      this.nameBadgeDlg.meta = ''
    },
    async exportNameBadgeWord() {
      const card = this.nameBadgeDlg && this.nameBadgeDlg.card
      if (!card || !card.available) {
        window.alert('请先等待胸卡加载完成')
        return
      }
      try {
        await exportBadgeWord(card)
      } catch (e) {
        window.alert(e.message || '导出 Word 失败')
      }
    },
    printNameBadgeDlg() {
      const card = this.nameBadgeDlg && this.nameBadgeDlg.card
      if (!card || !card.available) {
        window.alert('请先等待胸卡加载完成后再打印')
        return
      }
      printBadgeArea('admin-badge-print-area', card)
    },
    goBack() {
      this.$router.push('/content/activities')
    },
    goSeating() {
      if (!this.activityId) return
      this.$router.push(`/content/activity-registrations/${this.activityId}/seating`)
    },
    goLottery() {
      if (!this.activityId) return
      this.$router.push(`/content/activity-registrations/${this.activityId}/lottery`)
    },
    goSatisfactionResponses() {
      if (!this.activityId) return
      this.$router.push(`/content/activity-registrations/${encodeURIComponent(String(this.activityId))}/satisfaction-responses`)
    },
    onSearch() {
      this.rosterPage = 1
      this.loadRegs()
    },
    onResetFilters() {
      this.regFilterAttendeeKeyword = ''
      this.regFilterSubmitter = ''
      this.filterSourceLabel = ''
      this.filterActivityStatus = ''
      this.filterOrderStatusText = ''
      this.filterMemberTagId = ''
      this.filterSubmittedFrom = ''
      this.filterSubmittedTo = ''
      this.filterCancelledFrom = ''
      this.filterCancelledTo = ''
      this.rosterPage = 1
    },
    regStatusLabel(r) {
      const src = r.sourceType || r.source_type || ''
      const raw = String(r.statusCode || r.status_code || '').trim()
      if (src === 'FORM') {
        if (!raw || raw === 'SUBMITTED') return '表单已提交'
        return raw
      }
      const u = raw.toUpperCase()
      if (u === 'SIGNUP_REVIEWING') return '审核中'
      if (u === 'SIGNUP_REJECTED') return '已驳回'
      if (u === 'ORDER_PENDING') return '待支付（活动费）'
      if (u === 'ORDER_PAID_PENDING_REVIEW') return '活动费待审核'
      if (u === 'ORDER_CANCEL_PENDING') return '取消订单待审核'
      if (u === 'ATTENDABLE' || u === 'CHECKIN_OPEN') return '已报名（待参加）'
      if (u === 'EVENT_COMPLETED') return '已完成'
      if (u === 'REGISTERED' || u === 'APPROVED') return '已报名（待参加）'
      if (u === 'REVIEWING' || u === 'PENDING_REVIEW' || u === 'UNDER_REVIEW') return '审核中'
      if (u === 'REJECTED' || u === 'REJECT') return '已驳回'
      if (u === 'CANCELLED') return '已取消'
      if (u === 'ENDED') return '已结束'
      return raw || '—'
    },
    voucherAbs(url) {
      const u = String(url || '').trim()
      if (!u) return '#'
      if (u.startsWith('http://') || u.startsWith('https://')) return u
      return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`
    },
    orderStatusUpper(r) {
      const s = r.activityFeeOrderStatus || r.activity_fee_order_status || ''
      return String(s).trim().toUpperCase()
    },
    regOrderId(r) {
      const v = r.activityFeeOrderId != null ? r.activityFeeOrderId : r.activity_fee_order_id
      const n = Number(v)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    async loadRegs(opts) {
      if (!this.activityId) return
      const silent = !!(opts && opts.silent)
      try {
        const list = await getAdminCmsActivityRegistrationAttendees(this.activityId)
        this.attendeeRows = Array.isArray(list) ? list : []
        if (!silent) {
          this.applyRouteFocus()
        }
      } catch (e) {
        if (!silent) {
          this.errorMsg = e.message || '读取报名记录失败'
        }
        this.attendeeRows = []
      }
    },
    async downloadSignupTemplate() {
      if (!this.activityId) return
      try {
        await downloadAdminActivitySignupImportTemplate(this.activityId)
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '下载模板失败'
      }
    },
    pickSignupImport() {
      const el = this.$refs.signupImportInput
      if (el) el.click()
    },
    async onSignupImportFile(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      if (ev && ev.target) ev.target.value = ''
      if (!file || !this.activityId) return
      this.importingSignup = true
      this.errorMsg = ''
      this.okMsg = ''
      try {
        const res = await importAdminActivitySignupRoster(this.activityId, file)
        const ok = res && res.successCount != null ? res.successCount : 0
        const fail = res && res.failCount != null ? res.failCount : 0
        this.okMsg = `导入完成：成功 ${ok} 条，失败 ${fail} 条（导入后直接通过审核）`
        if (res && Array.isArray(res.errors) && res.errors.length) {
          const lines = res.errors.slice(0, 5).map((x) => `第${x.rowNum}行 ${x.name || ''}：${x.reason || ''}`)
          this.errorMsg = lines.join('；') + (res.errors.length > 5 ? '…' : '')
        }
        await this.loadRegs({ silent: true })
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '导入失败'
      } finally {
        this.importingSignup = false
      }
    },
    async exportCheckedInRoster() {
      if (!this.activityId) return
      this.exportingCheckin = true
      this.errorMsg = ''
      this.okMsg = ''
      try {
        await downloadAdminActivityCheckedInRosterCsv(this.activityId)
        this.okMsg = '已签到名单已下载'
      } catch (e) {
        this.errorMsg = e.message || '导出失败'
      } finally {
        this.exportingCheckin = false
      }
    },
    async stopStaffQrScan() {
      this.staffQrActive = false
      const sc = this._staffHtml5Qr
      this._staffHtml5Qr = null
      if (!sc) return
      try {
        await sc.stop()
      } catch {
        /* scanner may already be stopped */
      }
      try {
        sc.clear()
      } catch {
        /* ignore clear errors */
      }
    },
    async toggleStaffQrScan() {
      if (this.staffQrActive) {
        await this.stopStaffQrScan()
        return
      }
      this.staffQrErr = ''
      await this.stopStaffQrScan()
      this.staffQrActive = true
      await this.$nextTick()
      try {
        const { Html5Qrcode } = await import('html5-qrcode')
        const scanner = new Html5Qrcode('staff-qr-reader')
        this._staffHtml5Qr = scanner
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            this.staffPaste = String(decodedText || '').trim()
            this.staffCheckinMsg = '已扫码填入，可点击确认代签到'
            this.stopStaffQrScan()
          },
          () => {},
        )
      } catch (e) {
        this.staffQrErr = e.message || '无法打开摄像头（请使用 HTTPS 或 localhost，并授予权限）'
        await this.stopStaffQrScan()
      }
    },
    async doStaffCheckin() {
      if (!this.activityId) return
      const t = String(this.staffPaste || '').trim()
      if (!t) {
        this.staffCheckinMsg = '请先粘贴核验串'
        return
      }
      this.staffChecking = true
      this.staffCheckinMsg = ''
      this.errorMsg = ''
      try {
        const res = await staffCheckinActivity(this.activityId, t)
        this.staffCheckinMsg = (res && res.message) || '已处理'
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '代签到失败'
      } finally {
        this.staffChecking = false
      }
    },
    openReject(r) {
      const id = this.regOrderId(r)
      if (!id) return
      this.rejectOrderId = id
      this.rejectNote = ''
      this.showReject = true
    },
    async submitReject() {
      if (!this.rejectOrderId) return
      const note = String(this.rejectNote || '').trim()
      if (!note) {
        this.errorMsg = '请填写驳回原因'
        return
      }
      try {
        await rejectAdminMemberOrderOfflinePay(this.rejectOrderId, { note })
        this.showReject = false
        this.okMsg = '已驳回'
        await this.loadRegs()
      } catch (e) {
        this.errorMsg = e.message || '操作失败'
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  max-width: 100%;
  box-sizing: border-box;
}
.page-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.back-btn:hover {
  transform: translateX(-1px);
}
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.sub-line {
  margin: 0 0 10px 48px;
  font-size: 14px;
  color: #667085;
}
.tip {
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  line-height: 1.55;
  font-size: 13px;
}
.hidden-file {
  display: none;
}
.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.toolbar-row .toolbar-btn {
  min-width: 148px;
  min-height: 38px;
  padding: 0 14px;
  font-size: 13px;
  line-height: 1.2;
  border-radius: 8px;
  border: 1px solid transparent;
  white-space: nowrap;
  box-sizing: border-box;
}
.toolbar-row .toolbar-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.toolbar-btn--export-checkin {
  background: #2f6af6;
  color: #fff;
}
.toolbar-btn--export-roster {
  background: #0ea5e9;
  color: #fff;
}
.toolbar-btn--download-template {
  background: #8b5cf6;
  color: #fff;
}
.toolbar-btn--import-roster {
  background: #f97316;
  color: #fff;
}
.toolbar-btn--proxy-signup {
  background: #12b76a;
  color: #fff;
}
.toolbar-btn--seating {
  background: #7c3aed;
  color: #fff;
}
.toolbar-btn--lottery {
  background: #f59e0b;
  color: #fff;
}
.toolbar-btn--satisfaction {
  background: #2f7bff;
  color: #fff;
}
.tool-label {
  font-weight: 600;
  color: #344054;
  font-size: 13px;
}
.tool-fs {
  width: 260px;
  min-width: 220px;
}
.tool-fs--sm {
  width: 150px;
  min-width: 120px;
}
.tool-fs--md {
  width: 180px;
  min-width: 160px;
}
.tool-input {
  height: 38px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: #1d2939;
  background: #fff;
}
.filters {
  margin-bottom: 12px;
}
.roster-filters {
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}
.roster-filters .filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 12px 16px;
  width: 100%;
}
.roster-filters .filter-row--secondary {
  padding-top: 12px;
  border-top: 1px dashed #eaecf0;
}
.roster-filters .flt-input--attendee {
  width: 280px;
  min-width: 220px;
}
.roster-filters .flt-input--wide {
  width: 200px;
}
.roster-filters :deep(.fs-btn) {
  height: 40px;
  min-width: 150px;
  border-radius: 8px;
}
.col-sticky-right {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06);
}
.roster-table thead .col-sticky-right {
  z-index: 3;
  background: #f8fafc;
}
.col-seq,
.col-check {
  width: 72px;
  min-width: 72px;
  max-width: 72px;
  white-space: nowrap;
}
.col-status,
.col-name,
.col-phone,
.col-company,
.col-duty,
.col-datetime,
.col-submitter,
.col-tags {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
}
.col-status,
.col-phone,
.col-datetime {
  white-space: nowrap;
}
.col-name,
.col-company,
.col-duty,
.col-submitter,
.col-tags {
  white-space: normal;
  word-break: break-word;
}
.col-ops {
  width: 180px;
  min-width: 180px;
  max-width: 180px;
  white-space: normal;
}
.col-ops .action-stack {
  margin: 0 auto;
}
.col-tags .member-tag-pill {
  vertical-align: middle;
}
.td-nowrap {
  white-space: nowrap;
}
.sub {
  font-size: 12px;
  color: #667085;
  margin-top: 2px;
}
.channel-tag {
  display: inline-block;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: #6941c6;
  background: #f4e8ff;
}
.roster-batch-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 12px 0 8px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}
.roster-batch-bar--idle {
  background: transparent;
  border: none;
  padding: 0;
  margin: 8px 0;
}
.roster-batch-tip {
  font-size: 13px;
  color: #475467;
  margin-right: 8px;
}
.member-tag-pill {
  display: inline-block;
  margin: 2px 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  color: #175cd3;
  background: #eff8ff;
  border: 1px solid #b2ddff;
}
.export-field-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}
.export-field-group {
  margin-bottom: 14px;
}
.export-field-group-title {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  margin-bottom: 8px;
}
.tag-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.tag-chip--on {
  border-color: #84caff;
  background: #eff8ff;
}
.tag-chip input {
  margin: 0;
}
.tag-dialog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.tag-dialog-label {
  font-size: 13px;
  color: #475467;
}
.btn-act-tag {
  color: #175cd3;
  border-color: #b2ddff;
}
.order-cancel-at {
  font-size: 11px;
  color: #667085;
  margin-top: 4px;
  line-height: 1.35;
  white-space: nowrap;
}
.activity-checkin-at {
  font-size: 11px;
  color: #067647;
  margin-top: 4px;
  line-height: 1.35;
  white-space: nowrap;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}
.status-pill--dash,
.status-pill--unknown {
  background: #f2f4f7;
  color: #667085;
}
.status-pill--id-member {
  background: #e8fbef;
  color: #067647;
}
.status-pill--id-guest {
  background: #f2f4f7;
  color: #475467;
}
.status-pill--cancelled {
  background: #fef3f2;
  color: #b42318;
}
.status-pill--signup-rejected {
  background: #fee4e2;
  color: #912018;
}
.status-pill--signup-review {
  background: #fff7e6;
  color: #b54708;
}
.status-pill--cancel-review {
  background: #f4e8ff;
  color: #6941c6;
}
.status-pill--pending-pay {
  background: #eef4ff;
  color: #1d4ed8;
}
.status-pill--fee-review {
  background: #fff4ed;
  color: #c4320a;
}
.status-pill--fee-rejected {
  background: #ffe4e8;
  color: #c01048;
}
.status-pill--await-checkin {
  background: #f5f3ff;
  color: #6d28d9;
}
.status-pill--checked-in {
  background: #d1fadf;
  color: #027a48;
}
.status-pill--order-paid {
  background: #ccfbf1;
  color: #0f766e;
}
.status-pill--refund-pending {
  background: #fefbe8;
  color: #a15c07;
}
.status-pill--refunded {
  background: #d1fadf;
  color: #05603a;
}
/* 兼容弹窗等旧引用 */
.status-pill--active {
  background: #e8fbef;
  color: #067647;
}
.status-pill--review {
  background: #fff4ed;
  color: #c4320a;
}
.status-pill--pay {
  background: #eef4ff;
  color: #1d4ed8;
}
.status-pill--idle {
  background: #f2f4f7;
  color: #475467;
}
.status-pill--biz {
  background: #f5f3ff;
  color: #6d28d9;
}
.status-pill--danger {
  background: #fef3f2;
  color: #b42318;
}
.voucher-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
}
.checkin-mode-banner {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #eff8ff;
  border: 1px solid #b2ddff;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.55;
  color: #175cd3;
}
.checkin-mode-banner p {
  margin: 8px 0 0;
  color: #344054;
}
.staff {
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #eaecf0;
}
.staff-label {
  display: block;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
}
.staff-ta {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  font-family: ui-monospace, monospace;
  font-size: 13px;
}
.staff-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.5;
}
.staff-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.staff-msg {
  font-size: 13px;
  color: #067647;
}
.staff-err {
  font-size: 12px;
  color: #d92d20;
}
.staff-qr-reader {
  width: 100%;
  max-width: 320px;
  margin-top: 8px;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #fff;
}
.roster-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.roster-table th,
.roster-table td {
  box-sizing: border-box;
  border-bottom: 1px solid #edf0f5;
  padding: 10px 16px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
  background: #fff;
}
.roster-table thead th {
  background: #f8fafc;
  color: #344054;
  font-weight: 600;
  white-space: nowrap;
}
.roster-table tbody .col-sticky-right {
  background: #fff;
}
.ellipsis {
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.reg-count-cell {
  font-weight: 700;
  font-size: 15px;
  color: #101828;
}
.reg-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.reg-name {
  font-weight: 600;
}
.muted {
  color: #98a2b3;
}
.link {
  color: #175cd3;
}
.error {
  color: #d92d20;
  font-size: 13px;
  margin: 0 0 8px;
}
.voucher-dialog-error {
  margin: 12px 0 0;
}
.voucher-view-block + .voucher-view-block {
  margin-top: 16px;
}
.voucher-view-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}
.ok {
  color: #067647;
  font-size: 13px;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
}
.btn-sm {
  padding: 4px 10px;
  font-size: 11px;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.action-cell {
  white-space: normal;
  line-height: 1.5;
}
.action-cell .btn {
  margin: 4px 8px 4px 0;
}
.action-stack {
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  width: 148px;
  vertical-align: middle;
  text-align: center;
}
.action-stack .btn-act {
  width: 100%;
  min-height: 32px;
  margin: 0;
  padding: 0 8px;
  font-size: 11px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  white-space: nowrap;
  border: 1px solid transparent;
}
.action-stack .btn + .btn {
  margin-left: 0;
}
.action-stack .btn-act:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.action-stack .btn-act-edit {
  border-color: #d0d5dd;
  background: #fff;
  color: #344054;
}
.action-stack .btn-act-audit-signup {
  background: #2f6af6;
  color: #fff;
}
.action-stack .btn-act-cancel-audit {
  background: #7f56d9;
  color: #fff;
}
.action-stack .btn-act-cancel-rejected {
  background: #f4e8ff;
  border-color: #d6bbfb;
  color: #6941c6;
}
.action-stack .btn-act-cancel-reason {
  background: #fff;
  border-color: #d6bbfb;
  color: #6941c6;
}
.action-stack .btn-act-confirm-pay {
  background: #12b76a;
  color: #fff;
}
.action-stack .btn-act-upload-voucher {
  background: #0ea5e9;
  color: #fff;
}
.action-stack .btn-act-view-voucher {
  background: #fff;
  border-color: #84caff;
  color: #175cd3;
}
.action-stack .btn-act-refund-upload {
  background: #f79009;
  color: #fff;
}
.action-stack .btn-act-refund-done {
  background: #039855;
  color: #fff;
}
.action-stack .btn-act-signature-view {
  background: #fff;
  border-color: #b2ddff;
  color: #175cd3;
}
.action-stack .btn-act-signature-upload {
  background: #444ce7;
  color: #fff;
}
.action-stack .btn-act-badge {
  background: #fff;
  border-color: #99c7ff;
  color: #1849a9;
}
.signup-preview-pre {
  margin: 0;
  max-height: 62vh;
  overflow: auto;
  font-size: 12px;
  line-height: 1.45;
  background: #f8fafc;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
}
.linklike-btn {
  border: none;
  background: none;
  padding: 0;
  color: #175cd3;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.signup-preview-body {
  max-height: 62vh;
  overflow: auto;
  padding-right: 4px;
}
.signup-preview-scroll {
  max-height: 62vh;
  overflow: auto;
  padding-right: 4px;
}
.dlg-st-actions-col {
  width: 168px;
  white-space: nowrap;
}
.dlg-st-actions {
  vertical-align: middle !important;
}
.dlg-st-actions-inner {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.dlg-st-actions-inner .btn-sm {
  width: 100%;
}
.signup-version {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eaecf0;
}
.signup-version:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.version-meta {
  margin: 0 0 10px;
  font-size: 13px;
  color: #475467;
  font-weight: 600;
}
.dlg-person-seg {
  display: flex;
  gap: 8px;
  margin: 0 0 12px;
}
.dlg-person-seg .seg-btn {
  border: 1px solid #d0d5dd;
  background: #fff;
  color: #344054;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
}
.dlg-person-seg .seg-btn.on {
  border-color: #2f7bff;
  color: #175cd3;
  background: #eff4ff;
}
.dlg-partition-hint {
  font-size: 13px;
  color: #475467;
  line-height: 1.5;
  margin: 0 0 12px;
}
.signup-preview-empty-wrap {
  padding: 8px 0 4px;
}
.signup-preview-empty-wrap .muted {
  margin: 0 0 8px;
}
.dlg-cancel-note {
  font-size: 13px;
  line-height: 1.5;
  margin: -4px 0 12px;
  padding: 8px 10px;
  background: #fef3f2;
  border: 1px solid #fecdca;
  border-radius: 8px;
  color: #b42318;
}
.dlg-reg-checkin {
  font-size: 13px;
  color: #027a48;
  font-weight: 600;
  margin: 0 0 12px;
}
.dlg-block-submit {
  font-size: 12px;
  color: #667085;
  font-weight: 600;
  margin: 0 0 6px;
}
.partition-note {
  font-size: 12px;
  color: #667085;
  font-weight: 600;
  margin: 0 0 8px;
}
.dlg-empty-tab {
  font-size: 13px;
  margin: 0 0 12px;
}
.dlg-signup-table-wrap {
  overflow-x: auto;
  margin-top: 4px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #fff;
}
.dlg-signup-table {
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
  font-size: 12px;
}
.dlg-signup-table th,
.dlg-signup-table td {
  border: 1px solid #eaecf0;
  padding: 8px 10px;
  vertical-align: top;
  text-align: left;
}
.dlg-signup-table thead th {
  background: #f9fafb;
  color: #344054;
  font-weight: 700;
  white-space: nowrap;
}
.dlg-st-num {
  width: 48px;
  text-align: center !important;
  white-space: nowrap;
}
.dlg-st-center {
  text-align: center;
  white-space: nowrap;
}
.dlg-st-wrap {
  max-width: 140px;
  word-break: break-word;
}
.dlg-st-nowrap {
  white-space: nowrap;
}
.dlg-st-sub {
  font-size: 11px;
  color: #667085;
  font-weight: 600;
  margin-top: 4px;
}
.dlg-st-hold {
  color: #b54708;
  font-weight: 600;
}
.dlg-person {
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 12px 12px 4px;
  margin-bottom: 12px;
  background: #fcfcfd;
}
.dlg-person-title {
  margin: 0 0 10px;
  font-size: 15px;
  color: #101828;
}
.dlg-checkin {
  margin: -4px 0 10px;
  font-size: 13px;
  color: #027a48;
  font-weight: 600;
}
.dlg-checkin--hold {
  color: #b54708;
}
.dlg-fields {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.dlg-fields td {
  vertical-align: top;
  padding: 6px 4px;
  border-bottom: 1px solid #f2f4f7;
}
.dlg-k {
  width: 34%;
  color: #667085;
  font-weight: 600;
}
.dlg-v {
  color: #1d2939;
  white-space: pre-wrap;
  word-break: break-word;
}
.pill {
  display: inline-block;
  min-width: 52px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.fee-pill {
  min-width: 72px;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.pill-gray {
  color: #475467;
  background: #f2f4f7;
  border: 1px solid #eaecf0;
}
.pill-blue {
  color: #075985;
  background: #e0f2fe;
  border: 1px solid #bae6fd;
}
.pill-warn {
  color: #b54708;
  background: #fffaeb;
  border: 1px solid #fedf89;
}
.pill-info {
  color: #175cd3;
  background: #eff8ff;
  border: 1px solid #b2ddff;
}
.pill-amber {
  color: #93370d;
  background: #fff6ed;
  border: 1px solid #fddcab;
}
.mask--submission-audit {
  z-index: 1210;
}
.dialog {
  width: 480px;
  max-width: 92vw;
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.18);
}
/* 须写在 .dialog 之后：覆盖默认 480px，否则「查看报名信息」弹框一直被压窄 */
.dialog.dialog-signup-preview {
  width: min(1280px, 98vw);
  max-width: 98vw;
}
.dialog-voucher {
  width: min(560px, 94vw);
}
.dialog-badge-preview {
  width: min(580px, 96vw);
}
.dialog--shell .dialog-head {
  margin-bottom: 0;
  padding: 16px 18px 14px;
  border-bottom: 1px solid #eef2f7;
}
.badge-print-target {
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
}
.badge-preview-loading {
  padding: 48px 16px;
  text-align: center;
  font-size: 14px;
  color: #667085;
}
.badge-template-hint {
  margin: 0 0 12px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.55;
  color: #475467;
  background: #fffaeb;
  border: 1px solid #fedf89;
  border-radius: 8px;
}
.badge-template-hint strong {
  color: #344054;
}
.btn-link {
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  color: #175cd3;
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}
.btn-link:hover {
  color: #1849a9;
}
.dlg-signup-footer-actions {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #eaecf0;
}
.dlg-submission-audit .dlg-audit-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  font-size: 13px;
  margin: 12px 0;
}
.dlg-audit-subtitle {
  margin: 16px 0 8px;
  font-size: 14px;
  color: #101828;
}
.dlg-collect-list {
  border: 1px solid #eaecf0;
  border-radius: 8px;
  max-height: 280px;
  overflow-y: auto;
}
.dlg-collect-row {
  display: grid;
  grid-template-columns: minmax(120px, 28%) 1fr;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;
}
.dlg-collect-row:last-child {
  border-bottom: none;
}
.dlg-collect-l {
  color: #667085;
  font-weight: 600;
}
.dlg-collect-v {
  color: #1d2939;
  word-break: break-word;
}
.dlg-audit-actions {
  flex-wrap: wrap;
}
.dlg-audit-field {
  margin-top: 12px;
}
.dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.dialog h3 {
  margin: 0;
  font-size: 17px;
}
.dlg-meta {
  margin: 0 0 12px;
  font-size: 13px;
  color: #475467;
  line-height: 1.5;
}
.dlg-meta--warn {
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 10px 12px;
}
.badge-print-note {
  margin: 0 0 12px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.5;
  color: #475467;
  background: #f8fafc;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}
.dlg-cancel-reason {
  width: min(480px, 94vw);
}
.dlg-cancel-reason .dlg-audit-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px 16px;
  font-size: 13px;
  margin: 12px 0;
}
.dlg-cancel-reason-body {
  margin: 0;
  padding: 12px 14px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.55;
  color: #101828;
  white-space: pre-wrap;
  word-break: break-word;
}
.dlg-cancel-reason-body--reject {
  background: #fff6ed;
  color: #b54708;
}
.roster-row--focus td {
  background: #eef4ff !important;
  box-shadow: inset 0 0 0 2px #84adff;
}
.roster-row--focus .col-sticky-right {
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06), inset 0 0 0 2px #84adff;
}
.voucher-img-wrap {
  margin-bottom: 14px;
  text-align: center;
  max-height: 52vh;
  overflow: auto;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 10px;
  background: #fafafa;
}
.voucher-img {
  max-width: 100%;
  height: auto;
  vertical-align: middle;
  border-radius: 8px;
}
.dialog-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}
.dialog-actions--upload {
  align-items: center;
}
.hidden-file {
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.field span {
  font-weight: 600;
  font-size: 13px;
}
textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px;
}
.activity-subnav {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 16px;
}
.subnav-btn {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
}
.subnav-btn:hover {
  border-color: #98a2b3;
  background: #f9fafb;
}
.subnav-btn.on {
  border-color: #175cd3;
  background: #eff8ff;
  color: #175cd3;
}
.activity-form-panel {
  max-width: 560px;
  padding: 4px 0 20px;
}
.form-intro {
  margin: 0 0 16px;
}
.activity-form-panel .form-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}
.activity-form-panel .field-label {
  font-weight: 600;
  font-size: 13px;
  color: #344054;
  min-width: 96px;
}
.activity-form-panel .form-select {
  flex: 1;
  min-width: 220px;
  max-width: 100%;
  height: 38px;
}
.activity-form-panel .form-check {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  font-size: 14px;
  color: #344054;
  line-height: 1.45;
  cursor: pointer;
}
.activity-form-panel .form-check input {
  margin-top: 3px;
  flex-shrink: 0;
}
.activity-form-panel .form-actions {
  margin-top: 4px;
}
</style>
